import React from 'react'
import QuoteConfirmationModal from './QuoteConfirmationModal.jsx';
import { optimizeImageSrc } from '../utils/shared.js';

// Rebuild Matrix Data Structure
const REBUILD_MATRIX_DATA = {
    "Powertrain & Drivetrain": ["Engine", "Transmission", "Drive", "Differential", "Clutch / Torque Converter", "Turbo / Supercharger", "Exhaust System", "Pump Drive", "Custom 1 (Powertrain)", "Custom 2 (Powertrain)", "Custom 3 (Powertrain)", "Custom 4 (Powertrain)", "Custom 5 (Powertrain)"],
    "Hydraulic, Fuel & Fluid Systems": ["Hydraulic System (Pumps, Motors, Valves, Piping)", "Fuel System (Pump, Sensors, Valves, Piping)", "Diesel Supply / Tanks", "Piping & Fittings", "Cooling System (Radiator, Fans, Piping)", "Lubrication Systems", "Fluid Flush & Replacement", "Air System (Pump, Motor, Valves, Sensors, Piping)", "Feed Beam", "Stabilizers", "Reeler", "Custom 1 (Hydraulic)", "Custom 2 (Hydraulic)", "Custom 3 (Hydraulic)", "Custom 4 (Hydraulic)", "Custom 5 (Hydraulic)"],
    "Electrical & Electronic Systems": ["Wiring", "ECU / Control Modules", "Sensors & Actuators", "Battery & Charging System", "Starter & Alternator", "Display Screens & Instrument Panels", "Switches, Joysticks & Controls", "GPS / Telematics Systems", "Lights", "CAS Level 9 System", "Slip Ring Cabinet", "Custom 1 (Electrical)", "Custom 2 (Electrical)", "Custom 3 (Electrical)", "Custom 4 (Electrical)", "Custom 5 (Electrical)"],
    "Structure, Chassis & Frame": ["Mainframe", "MAST/FEED", "Cab / ROPS / FOPS", "Articulated Carrier", "Custom 1 (Structure)", "Custom 2 (Structure)", "Custom 3 (Structure)", "Custom 4 (Structure)", "Custom 5 (Structure)"],
    "Safety Systems": ["Fire Suppression", "Emergency Stop Systems", "Steps, Handrails & Guarding", "Seat & Seatbelt", "Custom 1 (Safety)", "Custom 2 (Safety)", "Custom 3 (Safety)", "Custom 4 (Safety)", "Custom 5 (Safety)"],
    "Wear Components & Running Gear": ["Tyres", "Rims & Wheels", "GET (Ground Engaging Tools)", "Bearings & Bushes", "Pins & Liners", "Tracks / Undercarriage (If Applicable)", "Custom 1 (Wear)", "Custom 2 (Wear)", "Custom 3 (Wear)", "Custom 4 (Wear)", "Custom 5 (Wear)"],
    "Bodywork, Cab & Visibility": ["Bodywork & Paint", "Windows", "Interior Trim", "Decals & Branding", "Corrosion Treatment", "Custom 1 (Bodywork)", "Custom 2 (Bodywork)", "Custom 3 (Bodywork)", "Custom 4 (Bodywork)", "Custom 5 (Bodywork)"],
    "Compliance, Legal & Identification": ["DMR Compliance", "Roadworthiness / Safety Certification", "Signage", "Steering Wheel", "Pedals", "Custom 1 (Compliance)", "Custom 2 (Compliance)", "Custom 3 (Compliance)", "Custom 4 (Compliance)", "Custom 5 (Compliance)"]
};

const COMPONENT_TYPES = {
    "Powertrain & Drivetrain": "A",
    "Hydraulic, Fuel & Fluid Systems": "B",
    "Electrical & Electronic Systems": "C",
    "Structure, Chassis & Frame": "D",
    "Safety Systems": "E",
    "Wear Components & Running Gear": "F",
    "Bodywork, Cab & Visibility": "G",
    "Compliance, Legal & Identification": "H"
};

// --- NEW: Helper to format description with line breaks ---
function formatDescription(description) {
    if (!description) return null;
    const lines = description.split('\n');
    return lines.map((line, i) => (
        <React.Fragment key={i}>
            {line}
            {i < lines.length - 1 && <br />}
        </React.Fragment>
    ));
}

// --- NEW: Internal Modal for viewing full variant specs ---
function VariantSpecsModal({ variant, onClose }) {
    if (!variant) return null;

    // Merge specs if available (for conveyor belts)
    const data = { ...variant, ...(variant.specs || {}) };

    // Filter out internal fields and empty values for display
    const displayFields = Object.entries(data).filter(([key, value]) => {
        const internalKeys = ['id', 'product_id', 'seller_id', 'category_id', 'created_at', 'updated_at', 'status', 'listing_type', 'listing_market', 'image_url_1', 'image_url_2', 'image_url_3', 'image_url_4', 'slug', 'name', 'description', 'price', 'quantity', 'is_price_on_request', 'listing_number', 'sales_contact_name', 'sales_contact_phone', 'sales_contact_email', 'location', 'seller_name', 'avg_rating', 'rating_count', 'category_name', 'badgeType', 'specs', 'image_url', 'type_description'];
        return !internalKeys.includes(key) && value && value !== 'NA' && value !== '0.00' && (typeof value === 'string' || typeof value === 'number');
    });

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                <div className="p-6 border-b flex justify-between items-start">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">{variant.bearing_type || variant.name}</h3>
                        <p className="text-blue-600 font-mono font-bold text-lg">{variant.part_number}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><i className="fas fa-times text-xl"></i></button>
                </div>
                <div className="p-6 overflow-y-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {displayFields.map(([key, value]) => (
                            <div key={key} className="bg-gray-50 p-3 rounded-lg">
                                <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{key.replace(/_/g, ' ')}</span>
                                <span className="block text-gray-900 font-medium break-words">{value}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="p-6 border-t bg-gray-50 rounded-b-xl flex justify-end">
                    <button onClick={onClose} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">Close</button>
                </div>
            </div>
        </div>
    );
}

// --- NEW: Helper to get rich descriptions for bearings ---
function getBearingRichDescription(type) {
    if (!type) return null;
    
    if (type === 'Cylindrical Roller Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Cylindrical Roller Bearings are engineered for high load-carrying capacity, precision, and durability in demanding industrial environments. These bearings use cylindrical rollers instead of balls, enabling them to handle heavier radial loads with minimal friction and high-speed performance.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>High Radial Load Capacity:</strong> The line contact between rollers and raceways allows these bearings to support much higher radial loads than ball bearings.</li>
                        <li><strong>High-Speed Capability:</strong> With minimal friction and efficient roller geometry, these bearings can operate at high speeds with reduced heat generation.</li>
                        <li><strong>Multiple Designs and Configurations:</strong> Available in types such as NU, N, NJ, and NUP, which offer flexibility in axial movement and fixed positioning depending on the design.</li>
                        <li><strong>Separability for Easy Mounting:</strong> Most bearings have separable inner and outer rings, simplifying installation, disassembly, and inspection.</li>
                        <li><strong>Precision Manufacturing:</strong> Manufactured with tight tolerances and surface finishes for smooth operation, long life, and minimal vibration.</li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Applications</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Mining & Cement</li>
                        <li>Electric Motors and Generators</li>
                        <li>Gearboxes and Transmissions</li>
                        <li>Steel Mills and Heavy Machinery</li>
                        <li>Machine Tools and CNC Equipment</li>
                        <li>Railway Axles and Rolling Stock</li>
                        <li>Pumps and Compressors</li>
                        <li>Pulp & Paper</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Double Direction Tapered Roller Thrust Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Tapered Roller Thrust bearings, are designed exclusively for axial load support. These bearings provide exceptional axial rigidity and are ideal for handling heavy loads on vertical shaft. Standard designs feature machined brass cages, ensuring durability and reliable performance.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Exclusive Axial Load Support:</strong> Designed specifically to handle heavy axial loads, not radial loads.</li>
                        <li><strong>High Axial Rigidity:</strong> Provides exceptional stiffness for precise axial positioning under high load.</li>
                        <li><strong>Durable Machined Brass Cages:</strong> Ensures reliable performance and long service life in demanding conditions.</li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Applications</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Mining</li>
                        <li>Marine Propulsion Systems</li>
                        <li>Steel and Paper Mills</li>
                        <li>Vertical Pumps and Compressors</li>
                        <li>Machine Tool Spindles</li>
                        <li>Oil Drilling Rigs and Equipment</li>
                        <li>Rotary Tables and Industrial Turntables</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Double Row Angular Contact Ball Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG offers a comprehensive range of Double Row Angular Contact Ball Bearings, engineered to provide high performance and reliability across various applications. These bearings are designed to accommodate both radial and axial loads in both directions, making them particularly suitable for applications requiring rigid axial guidance.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Key Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Design and Construction:</strong> Double Row Angular Contact Ball Bearings feature inner and outer rings with raceways displaced relative to each other, forming an angle of contact between 15° and 45° concerning the bearing axis. This design enables the bearings to handle combined loads efficiently.</li>
                        <li><strong>Load Capacity:</strong> The dual-row configuration allows these bearings to support high radial loads and axial loads in both directions, enhancing their versatility in various industrial applications.</li>
                        <li><strong>Material and Quality:</strong> Manufactured from premium Chrome Steel SAE52100/100Cr6/GCr15 or Stainless Steel (420 and 440), these bearings ensure durability and resistance to wear. They are available in multiple quality grades, including ABEC 1, ABEC 3, ABEC 5, and ABEC 7, catering to different precision requirements.</li>
                        <li><strong>Seals and Shields:</strong> Available in open and 2RS (sealed) configurations, these bearings offer flexibility based on application needs, providing protection against contaminants and retaining lubrication.</li>
                        <li><strong>Cage Options:</strong> Equipped with machined brass cages, these bearings ensure optimal performance under varying operational conditions.</li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Applications</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Mining: Heavy-duty mud pumps, vertical turbines, drilling equipment</li>
                        <li>Metal Industry: Rolling mills and continuous casting machines</li>
                        <li>Fluid Machinery: Pumps and compressors</li>
                        <li>Aerospace Applications: Aircraft engines and landing gear systems</li>
                        <li>Machine Tool Applications: Spindle units and precision machinery</li>
                        <li>Pumps and Motors: Industrial pumps and electric motors</li>
                        <li>Railroad Applications: Axle boxes and traction motors</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Four Row Cylindrical Roller Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Four Row Cylindrical Roller Bearings are engineered for extremely high radial load capacity and are predominantly used in heavy-duty industrial applications, particularly rolling mills. The four-row design allows for optimal load distribution across multiple roller rows, resulting in high rigidity, excellent operational accuracy, and long service life under severe working conditions. These bearings are typically separable, allowing for easier mounting, inspection, and maintenance.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Exceptional Radial Load Capacity:</strong> The four-row configuration enables the bearing to support very high radial loads, making it ideal for heavy industrial machinery.</li>
                        <li><strong>High Rigidity and Precision:</strong> Provides excellent stiffness and dimensional stability, ensuring accurate shaft guidance under extreme loads.</li>
                        <li><strong>Optimized Roller Guidance:</strong> Cylindrical rollers are designed for line contact with raceways, reducing stress concentration and improving load distribution.</li>
                        <li><strong>Separable Design:</strong> Inner ring, outer ring, and roller assemblies can be mounted separately, simplifying installation and maintenance.</li>
                        <li><strong>Durable Cage Construction:</strong> Typically supplied with machined brass or steel cages for reliable performance in demanding environments.</li>
                        <li><strong>Designed for High-Speed Operation:</strong> Suitable for applications requiring both heavy load handling and relatively high rotational speeds.</li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Applications</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Mining and Mineral Processing Equipment</li>
                        <li>Rolling Mills (work rolls and backup rolls)</li>
                        <li>Hot and Cold Strip Mills</li>
                        <li>Continuous Casting Equipment</li>
                        <li>Large Industrial Presses</li>
                        <li>Steel and Metal Processing Plants</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Inner Ring Four Row Cylindrical Roller Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>Inner Ring Four Row Cylindrical Roller Bearings are designed for extreme radial load capacity in heavy-duty industrial applications where high precision shaft guidance is critical. KG Ultra inner ring allows to be paired with specific shaft arrangements or replaced independently, offering greater flexibility in rolling mill and large machinery designs.</p>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Self-Aligning Ball Bearing' || type === 'Self Aligning Ball Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Self-Aligning Ball Bearings are engineered to deliver exceptional performance in applications where shaft misalignment or deflection may occur. These bearings are designed to accommodate angular misalignment while maintaining smooth and efficient operation, ensuring reliability across various industrial applications.</p>
                <p>KG Ultra Self-Aligning Ball Bearings consist of two rows of balls and a common sphered raceway in the outer ring. This design allows the bearings to self-align, compensating for shaft misalignment and minimizing friction.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Self-Alignment Capability:</strong> Automatically compensates for shaft misalignment and deflection.</li>
                        <li><strong>Low Friction:</strong> Optimized for smooth operation and reduced heat generation.</li>
                        <li><strong>High-Speed Performance:</strong> Suitable for applications requiring consistent high-speed operation.</li>
                        <li><strong>Durability:</strong> Manufactured using high-quality materials for extended bearing life.</li>
                        <li><strong>Versatile Design:</strong> Available in open and sealed variants for different environmental conditions.</li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Applications</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Mining & Cement</li>
                        <li>Pulp & Paper mills</li>
                        <li>Fans and blowers</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Single Row Deep Groove Ball Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Bearings offers a comprehensive range of Deep Groove Ball Bearings (DGBBs), one of the most widely used bearing types across industries. Engineered for high-speed performance, low friction, and durability, our bearings are designed to handle both radial and axial loads, ensuring smooth, quiet and reliable operation.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>High Load Carrying Capacity:</strong> DGBBs are designed to handle both radial and moderate axial loads in both directions, making them versatile for a wide range of applications</li>
                        <li><strong>Low Friction and Noise:</strong> These bearings are manufactured with precision tolerances and high-quality materials to ensure smooth rotation, minimal vibration, and reduced noise levels</li>
                        <li><strong>Long Service Life with Reliable Sealing Options:</strong> Available with metal shields or rubber seals, KG Ultra DGBBs offer effective protection against contaminants, ensuring extended bearing life and dependable performance</li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Applications</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Mining & Cement : Heavy-duty mud pumps, vertical turbines, drilling equipment</li>
                        <li>Heavy-Duty Electric Motors : Suitable for handling higher radial loads than single row bearings</li>
                        <li>Industrial Gearboxes and Transmissions : Offers enhanced load capacity in compact spaces</li>
                        <li>Elevators and Hoists : Supports combined loads with improved stability</li>
                        <li>Conveyors and Material Handling Systems : Provides durable performance under continuous use</li>
                        <li>Fans and Blowers : Ensures long-lasting, smooth operation in high-load rotating systems</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    if (type === 'Spherical Roller Bearing') {
        return (
            <div className="text-gray-600 text-sm space-y-4 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p>KG Ultra Spherical Roller Bearings feature two rows of symmetrical rollers and a common sphered raceway in the outer ring. This unique design allows the bearings to self-align and maintain performance even in cases of shaft deflection and misalignment, accommodating heavy radial and axial loads. These bearings are designed to deliver superior load-carrying capacity, exceptional durability, and reliable performance under the most demanding conditions.</p>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Features</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Self-Alignment Capability:</strong> Automatically compensates for shaft misalignment or housing deflection.</li>
                        <li><strong>High Radial and Moderate Axial Load Capacity:</strong> Designed to handle heavy radial loads and moderate axial loads in both directions.</li>
                        <li><strong>Adjustable Clearance:</strong> Axial clearance can be adjusted during mounting, allowing precise control over performance and preload.</li>
                        <li><strong>Separable Components:</strong> The inner (cone) and outer (cup) rings can be mounted separately for ease of assembly and inspection.</li>
                        <li><strong>Long Service Life:</strong> Manufactured with high-quality steel and precision engineering for durability and reliability under extreme conditions.</li>
                    </ul>
                </div>
                <div>
                    <h4 className="font-bold text-gray-800 mb-1">Applications</h4>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Mining and Quarry Equipment</li>
                        <li>Steel and Paper Mills</li>
                        <li>Pulp and Paper Machinery</li>
                        <li>Construction Equipment</li>
                        <li>Heavy-Duty Fans and Blowers</li>
                        <li>Gearboxes and Conveyors</li>
                        <li>Cement Processing Plants</li>
                    </ul>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-bold text-blue-800 mb-2">Why Choose KG Ultra Bearings?</h4>
                    <ul className="list-disc pl-5 space-y-1 text-blue-900">
                        <li><strong>ISO tolerance class 6:</strong> ISO 492, P6, Tighter tolerance than normal</li>
                        <li><strong>Large assortments:</strong> With ISO 5593:2023 dimensions and control</li>
                        <li><strong>Premium quality steel:</strong> Made with GCr15 equivalent to AISI 52100 and DIN 100Cr6 (SKF grade 3)</li>
                        <li><strong>Unparalleled technical and after sales support</strong></li>
                    </ul>
                </div>
            </div>
        );
    }
    return null;
}

const DetailDisplay = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return <p><span className="font-semibold">{label}:</span> {value}</p>;
};

const formatPrice = (price) => {
    const num = parseFloat(price);
    return isNaN(num) ? 'R 0.00' : `R ${num.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// --- NEW: Curated Rebuild Options per Category ---
const BUYER_REBUILD_OPTIONS = {
    "Powertrain & Drivetrain": ['Zero Hour Rebuild', 'Service Exchange', 'Reseal & Bearings', 'Dyno Test Only', 'Supply As-Is'],
    "Hydraulic, Fuel & Fluid Systems": ['Full Rebuild & Chrome', 'Reseal Only', 'Pressure Test', 'Supply As-Is'],
    "Electrical & Electronic Systems": ['Full Rewire & New Components', 'Repair Existing', 'Test Functionality', 'Supply As-Is'],
    "Structure, Chassis & Frame": ['Crack Repair & Line Boring', 'Sandblast & Paint', 'NDT Certification', 'Supply As-Is'],
    "Wear Components & Running Gear": ['Replace with New', 'Refurbish/Hardface', 'Supply As-Is'],
    "Bodywork, Cab & Visibility": ['Full Cab Refurb', 'Glass & Trim Only', 'Respray Only', 'Supply As-Is'],
    "Safety Systems": ['Install New', 'Test & Certify', 'Supply As-Is'],
    "Compliance, Legal & Identification": ['Roadworthy/Certificate', 'Check Only', 'N/A']
};

/**
 * Modal for configuring the rebuild request.
 * Now supports optional notes and filters "Other" items based on seller input.
 */
function RebuildConfigurationModal({ isOpen, onClose, rebuildConfig, onConfigChange, sellerRebuildDetails }) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                <div className="p-6 border-b flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <div>
                        <h3 className="text-2xl font-bold text-gray-900">Configure Rebuild Request</h3>
                        <p className="text-sm text-gray-600">Select the components you want to include in the Request for Quote (RFQ).</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <i className="fas fa-times text-2xl"></i>
                    </button>
                </div>
                
                <div className="flex-grow overflow-y-auto p-6 bg-gray-50">
                    <div className="space-y-6">
                        {Object.entries(REBUILD_MATRIX_DATA).map(([category, items]) => {
                            // Filter items: Only show "Other" fields if the seller has actually provided details for them.
                            const visibleItems = items.filter(item => {
                                if (!item.startsWith('Other')) return true;
                                const sDetails = sellerRebuildDetails[item];
                                return sDetails && (sDetails.selected || sDetails.other_name);
                            });

                            if (visibleItems.length === 0) return null;

                            return (
                                <div key={category} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                                <div className="bg-gray-100 px-6 py-3 border-b border-gray-200">
                                    <h4 className="font-bold text-gray-800 text-lg">{category}</h4>
                                </div>
                                <div className="p-6">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {visibleItems.map(item => {
                                            const isSelected = !!rebuildConfig[item];
                                                const config = rebuildConfig[item] || {};
                                            const sellerDetails = sellerRebuildDetails[item];
                                            const displayName = (item.startsWith('Other') && sellerDetails?.other_name) ? sellerDetails.other_name : item;
                                            
                                            // Determine options based on category
                                            const options = BUYER_REBUILD_OPTIONS[category] || ['New', 'Refurb', 'AS-IS', 'Service Only'];

                                            return (
                                                <div key={item} className={`p-4 rounded-lg border transition-all ${isSelected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}>
                                                    <div className="flex flex-col gap-3">
                                                        <div className="flex items-start justify-between">
                                                            <label className="flex items-center cursor-pointer">
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={isSelected} 
                                                                    onChange={(e) => onConfigChange(item, 'condition', e.target.checked ? options[0] : null)} 
                                                                    className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500 mt-0.5"
                                                                />
                                                                    <span className="ml-3 font-bold text-gray-800 select-none">{displayName}</span>
                                                            </label>
                                                        </div>

                                                        {/* Seller Status */}
                                                        {sellerDetails && (
                                                            <div className="ml-8 text-xs text-gray-600 bg-white p-2 rounded border border-gray-200">
                                                                <span className="font-bold text-gray-500 uppercase tracking-wide block mb-1">Current Status</span>
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className={`px-2 py-0.5 rounded font-semibold ${
                                                                        sellerDetails.condition === 'New' ? 'bg-green-100 text-green-800' :
                                                                        sellerDetails.condition === 'Refurb' ? 'bg-blue-100 text-blue-800' :
                                                                        'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                        {sellerDetails.condition || 'Not Specified'}
                                                                    </span>
                                                                    {sellerDetails.hours && <span className="text-gray-500">| {sellerDetails.hours} Hrs</span>}
                                                                </div>
                                                                {sellerDetails.comment && <p className="italic mt-1 text-gray-500 line-clamp-2">"{sellerDetails.comment}"</p>}
                                                            </div>
                                                        )}

                                                        {/* Buyer Selection */}
                                                        {isSelected && (
                                                            <div className="ml-8 mt-2">
                                                                <span className="text-xs font-bold text-blue-600 uppercase tracking-wide block mb-2">Requested Scope</span>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {options.map(opt => (
                                                                        <label key={opt} className={`cursor-pointer px-3 py-1.5 rounded-md text-xs font-bold border transition-all ${config.condition === opt ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`}>
                                                                                <input type="radio" name={`opt-${item}`} value={opt} checked={config.condition === opt} onChange={() => onConfigChange(item, 'condition', opt)} className="hidden" />
                                                                            {opt}
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                                    
                                                                    {/* Optional Request / Notes Input */}
                                                                    <div className="mt-3">
                                                                        <label className="text-xs font-bold text-gray-500 block mb-1">Optional Request / Notes</label>
                                                                        <input 
                                                                            type="text" 
                                                                            value={config.notes || ''} 
                                                                            onChange={(e) => onConfigChange(item, 'notes', e.target.value)}
                                                                            className="w-full text-xs border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                                                            placeholder="Specific requirements..."
                                                                        />
                                                                    </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )})}
                    </div>
                </div>
                
                <div className="p-6 border-t bg-white rounded-b-xl flex justify-end gap-4">
                    <button onClick={onClose} className="px-6 py-3 bg-gray-200 text-gray-800 font-bold rounded-lg hover:bg-gray-300 transition-colors">
                        Cancel
                    </button>
                    <button onClick={onClose} className="px-6 py-3 primary-gradient text-white font-bold rounded-lg hover:opacity-90 transition-opacity shadow-lg">
                        Save Configuration
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function ProductModal({ product, isOpen, onClose, user, onProductUpdate, onAddToBasket, onShowLogin }) {

        if (!isOpen || !product) return null;

        // --- NEW: Helper function to gate actions for guest users ---
        const gateAction = () => {
            if (!user) {
                // Construct a URL that will trigger the modal to reopen after login
                const redirectUrl = `${window.location.pathname}?viewProduct=${product.id}&viewType=${product.listingType}`;
                sessionStorage.setItem('redirectAfterLogin', redirectUrl);
                onShowLogin();
                return true; // Action is gated
            }
            return false; // User is logged in, proceed
        };

        const cleanPath = React.useCallback((path) => {
            if (!path) return null;
            // Allow web URLs and data URIs
            if (path.match(/^(http|https|data):/i)) return path;
            
            // Strip query parameters (e.g., cache-busting timestamps like ?t=1234567890)
            // before processing to ensure consistent path matching
            let clean = path.split('?')[0];
            
            // 1. Decode URI component to handle %20 correctly
            clean = decodeURIComponent(clean).replace(/\\/g, '/').trim();
            
            // 2. Remove full project path if present at the beginning
            if (clean.startsWith('/miners-market-main/')) {
                clean = clean.substring('/miners-market-main/'.length);
            }
            
            // Strategy: Locate 'uploads/' or 'images/' to anchor the path relative to project root
            const anchorMatch = clean.match(/(uploads\/|images\/|miscellaneous items\/)/i);
            if (anchorMatch) {
                clean = clean.substring(anchorMatch.index);
            }
            if (clean.includes(':')) return null;
            // Ensure absolute path starting with /
            if (!clean.startsWith('/')) {
                clean = '/' + clean;
            }
            return clean;
        }, []);

        const productImages = React.useMemo(() => {
            if (!product) return [];
            const allImages = [];
            if (product.images && Array.isArray(product.images)) {
                allImages.push(...product.images);
            }
            // --- NEW: Handle Media Array ---
            if (product.media && Array.isArray(product.media)) {
                const mediaImages = product.media.filter(m => m.media_type === 'image').map(m => m.file_path);
                allImages.push(...mediaImages);
            }
            for (let i = 1; i <= 10; i++) {
                if (product[`image_url_${i}`]) allImages.push(product[`image_url_${i}`]);
            }
            // --- REFACTORED: Non-destructive image logic for bearings ---
            let finalImages = [...new Set(allImages.map(p => cleanPath(p)).filter(Boolean))];
            
            if (product.bearing_type) { // Bearing logic - check for type instead of fragile ID
                const hasUserImages = finalImages.length > 0;

                if (product.bearing_type === 'Double Direction Tapered Roller Thrust Bearing') {
                    if (!hasUserImages) {
                        finalImages = ['images/products/Tapered Roller Thrust Bearings.png'];
                    }
                } else if (product.bearing_type.includes('Spherical Roller Bearing')) {
                    if (!hasUserImages) {
                        finalImages = [
                            'Miscellaneous Items/KG ULTRA BEARING IMAGES/Spherical roller bearing.png',
                            'Miscellaneous Items/KG ULTRA BEARING IMAGES/Spherical roller bearing box.png'
                        ];
                    }
                } else if (product.bearing_type === 'Self-Aligning Ball Bearing' || product.bearing_type === 'Self Aligning Ball Bearing') {
                    if (!hasUserImages) {
                        finalImages = [
                            'images/products/Self-Aligning Ball Bearing.png',
                            'images/products/Self-Aligning Ball Bearings Box.png'
                        ];
                    }
                } else {
                    // For other bearings, just add the packaging if it's not there
                    if (product.bearing_type === 'Inner Ring Four Row Cylindrical Roller Bearing') {
                        const pkgImage = 'images/products/Inner Ring Four Row Cylindrical Roller Bearing Packaging.png';
                        if (!finalImages.includes(pkgImage)) finalImages.push(pkgImage);
                    }
                const woodenBoxImage = 'images/logo/MM - Logo.jpeg';
                    if (!finalImages.includes(woodenBoxImage)) {
                        finalImages.push(woodenBoxImage);
                    }
                }
            }
            
            return finalImages;
        }, [product, cleanPath]);

        // State for the main image display and active tab
        const [mainImage, setMainImage] = React.useState(productImages.length > 0 ? productImages[0] : null);
        const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

        const handleImageNav = (e, direction) => {
            if (e && e.stopPropagation) e.stopPropagation();
            const newIndex = (currentImageIndex + direction + productImages.length) % productImages.length;
            setCurrentImageIndex(newIndex);
            setMainImage(productImages[newIndex]);
        };

        const [touchStart, setTouchStart] = React.useState(0);
        const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
        const handleTouchEnd = (e) => {
            const touchEnd = e.changedTouches[0].clientX;
            const diff = touchStart - touchEnd;
            if (Math.abs(diff) > 50) handleImageNav(null, diff > 0 ? 1 : -1);
        };

        const handleShare = async (e) => {
            if (e) e.stopPropagation();
            const shareUrl = `${window.location.origin}/${product.listingType === 'master_bearing' ? `bearing/${product.slug}` : `?viewProduct=${product.id}&viewType=${product.listingType}`}`;
            
            const shareText = `Check out this listing on Miners Market:\n\n${product.name}\nListing #: ${product.listing_number}\n\n${shareUrl}`;

            // Try Web Share API first (mobile devices)
            if (navigator.share) {
                try {
                    await navigator.share({
                        title: product.name,
                        text: shareText,
                        url: shareUrl
                    });
                    return;
                } catch (err) {
                    if (err.name !== 'AbortError') {
                        console.warn('Web Share API failed, falling back to clipboard:', err);
                    }
                }
            }

            // Fallback: Copy to clipboard with user feedback
            try {
                await navigator.clipboard.writeText(shareText);
                
                // Create a temporary toast notification
                const toast = document.createElement('div');
                toast.className = 'fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
                toast.textContent = 'Link copied to clipboard!';
                document.body.appendChild(toast);
                
                setTimeout(() => {
                    toast.style.opacity = '0';
                    toast.style.transition = 'opacity 0.3s';
                    setTimeout(() => document.body.removeChild(toast), 300);
                }, 2000);
            } catch (err) {
                console.error('Clipboard copy failed:', err);
                alert('Failed to copy link. Please copy manually:\n\n' + shareText);
            }
        };

        const [activeTab, setActiveTab] = React.useState('details');
        const [isProcessing, setIsProcessing] = React.useState(false); // Generic processing state
        const [actionError, setActionError] = React.useState(null);
        const [actionSuccess, setActionSuccess] = React.useState(null);
        const [actionLoading, setActionLoading] = React.useState(null);
        const [selectedQuantity, setSelectedQuantity] = React.useState(1);
        const [showPriceBreakdown, setShowPriceBreakdown] = React.useState(false);
        const [showContactModal, setShowContactModal] = React.useState(false);
        const [showInquiryModal, setShowInquiryModal] = React.useState(false);
        const [showDieselQuoteModal, setShowDieselQuoteModal] = React.useState(false);
        const [imageToEdit, setImageToEdit] = React.useState(null); // State for the blur editor
        const [showWatermarkSelector, setShowWatermarkSelector] = React.useState(false); // State for watermark selector
        const [selectedImageForWatermark, setSelectedImageForWatermark] = React.useState(null); // Selected image for watermark
        const [isWatermarkProcessing, setIsWatermarkProcessing] = React.useState(false); // Watermark processing state
        const [priceRequested, setPriceRequested] = React.useState(false);
        const [quoteRequested, setQuoteRequested] = React.useState(false); // NEW: State for quote requests
        const [showQuoteConfirmModal, setShowQuoteConfirmModal] = React.useState(false); // NEW: State for the confirmation modal
        const [itemAdded, setItemAdded] = React.useState(false); // NEW: State for basket feedback
        const [showSpecsModal, setShowSpecsModal] = React.useState(false); // NEW: State for specs modal
        const [showRebuildConfigModal, setShowRebuildConfigModal] = React.useState(false); // NEW: State for rebuild config modal
        
        // --- NEW: State for Complex Consumables ---
        const [availableSizes, setAvailableSizes] = React.useState(null); // Will hold parsed JSON if valid
        const [selectedSizes, setSelectedSizes] = React.useState({}); // { "M": 2, "L": 1 }

        // --- NEW: State for Rebuild Configuration ---
        const [rebuildConfig, setRebuildConfig] = React.useState({}); // { "Engine": "New", "Transmission": "Refurb" }
        const [sellerRebuildDetails, setSellerRebuildDetails] = React.useState({}); // Data from the seller

        // StarRating helper component to display star ratings
        const StarRating = ({ rating = 0, count = 0 }) => {
            const fullStars = Math.floor(rating);
            const halfStar = rating % 1 >= 0.5;
            const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

            if (count === 0) {
                return <span className="text-sm text-gray-500">No ratings yet.</span>;
            }

            return (
                <div className="flex items-center space-x-2">
                    <div className="flex text-amber-400">
                        {[...Array(fullStars)].map((_, i) => <i key={`full-${i}`} className="fas fa-star"></i>)}
                        {halfStar && <i className="fas fa-star-half-alt"></i>}
                        {[...Array(emptyStars)].map((_, i) => <i key={`empty-${i}`} className="far fa-star"></i>)}
                    </div>
                    <span className="text-sm text-gray-600">
                        {parseFloat(rating).toFixed(1)} ({count} {count === 1 ? 'rating' : 'ratings'})
                    </span>
                </div>
            );
        };

        // This effect ensures that when a new product is opened in the modal,
        // the main image and active tab are reset to the new product's defaults.
        React.useEffect(() => {
            if (product && productImages) {
                // Reset all state when the product changes
                setMainImage(productImages[0] || null);
                setCurrentImageIndex(0);
                setActiveTab('details');
                setSelectedQuantity(1);
                setShowPriceBreakdown(false);
                setActionError(null);
                setActionSuccess(null);
                // Check if price has been requested for this product
                const requested = localStorage.getItem(`price_requested_${product.listingType}_${product.id}`);
                setPriceRequested(!!requested);
                // NEW: Check if a quote has been requested
                setQuoteRequested(false); // Reset on new product;
                setShowQuoteConfirmModal(false);
                setItemAdded(false); // Reset basket feedback
                setShowSpecsModal(false);
                setShowRebuildConfigModal(false);
                
                // --- NEW: Check for complex size data ---
                if (product.listingType === 'consumable' && product.size && product.size.startsWith('{')) {
                    try {
                        const parsed = JSON.parse(product.size);
                        setAvailableSizes(parsed);
                        setSelectedSizes({});
                    } catch (e) {
                        setAvailableSizes(null);
                    }
                } else {
                    setAvailableSizes(null);
                }
                setRebuildConfig({}); // Reset rebuild config

                // --- NEW: Parse Seller's Rebuild Details ---
                if (product.listingType === 'rebuild' && product.rebuild_details) {
                    try {
                        const details = typeof product.rebuild_details === 'string' ? JSON.parse(product.rebuild_details) : product.rebuild_details;
                        setSellerRebuildDetails(details || {});
                    } catch (e) {
                        console.error("Error parsing rebuild details", e);
                        setSellerRebuildDetails({});
                    }
                } else {
                    setSellerRebuildDetails({});
                }
            }
        }, [product, productImages]); // Dependency array ensures this runs when the product prop changes

        // Effect to handle browser history for back button functionality
        React.useEffect(() => {
            if (isOpen) {
                // Push a new state to history when the modal opens
                window.history.pushState({ modal: true }, '');

                const handlePopState = (event) => {
                    // Disabled - prevent unexpected modal closures
                    // If the state is not our modal state, it means the user pressed back
                    // if (!event.state || !event.state.modal) {
                    //     onClose();
                    // }
                };

                window.addEventListener('popstate', handlePopState);
                return () => {
                    window.removeEventListener('popstate', handlePopState);
                    // Remove the modal history entry if closed manually
                    // if (window.history.state && window.history.state.modal) window.history.back();
                };
            }
        }, [isOpen, onClose]);

        // Track KG product view
        React.useEffect(() => {
            if (isOpen && product) {
                // Check if this is a KG product (seller_id = 328 or category = miscellaneous)
                const isKGProduct = product.seller_id === 328 ||
                    (product.listing_market === 'miscellaneous') ||
                    (product.category_id === 126);

                if (isKGProduct) {
                    const categoryMap = {
                        'filter': 'filters',
                        'bearing': 'bearings',
                        'conveyor': 'conveyor-belts',
                        'grease': 'greases',
                        'kg-pps': 'kg-pps',
                        'diesel': 'diesel'
                    };
                    const category = categoryMap[product.listingType] || 'general';

                    try {
                        const payload = JSON.stringify([{
                            product_id: product.id,
                            product_type: product.listingType || 'general',
                            product_name: product.name,
                            category: category,
                            seller_id: product.seller_id || 328
                        }]);
                        navigator.sendBeacon('/api/track_kg_product_view.php', payload);
                    } catch (e) {
                        console.error("Could not track KG product view:", e);
                    }
                }
            }
        }, [isOpen, product]);

        const documents = [
            { name: 'User Manual', url: product.doc_manual_url },
            { name: 'Data Pack', url: product.doc_datasheet_url },
            { name: 'Certificate of Conformity', url: product.doc_conformity_url },
            { name: 'Calibration Certificate', url: product.doc_calibration_url },
            { name: 'Warranty Card', url: product.doc_warranty_url },
            { name: 'Service History', url: product.doc_service_history_url },
            { name: 'Factory Test Report', url: product.doc_factory_test_url },
            { name: 'Oil Analysis Report', url: product.doc_oil_report_url },
        ];

        // --- NEW: Merge documents from 'media' array ---
        if (product.media && Array.isArray(product.media)) {
            product.media.filter(m => m.media_type === 'document').forEach(doc => {
                documents.push({ name: doc.display_name || doc.category, url: doc.file_path });
            });
        }

        // Parse checklist items safely for display
        const checklistItems = React.useMemo(() => {
            if (!product.component_checklist) return null;
            try {
                const list = typeof product.component_checklist === 'string' ? JSON.parse(product.component_checklist) : product.component_checklist;
                return Array.isArray(list) && list.length > 0 ? list : null;
            } catch (e) {
                return null;
            }
        }, [product.component_checklist]);

        const validDocuments = documents.filter(doc => doc.url);

        const handleRequestPrice = async () => {
            if (gateAction()) return;
            setIsProcessing(true);
            setActionError(null);
            setActionSuccess(null);
    
            try {
                const response = await fetch('/api/request_price.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        product_id: product.id,
                        listing_type: product.listingType || product.listing_type || 'standard'
                    })
                });
    
                const text = await response.text();
                let result = {};
                try {
                    result = text ? JSON.parse(text) : {};
                } catch (e) {
                    result = { error: 'Invalid response from server' };
                }
    
                if (!response.ok) {
                    throw new Error(result.error || `Request failed with status ${response.status}`);
                }
    
                setPriceRequested(true);
                localStorage.setItem(`price_requested_${product.listingType}_${product.id}`, 'true');
                setActionSuccess(result.message);
            } catch (err) {
                // The API now sends specific error messages for 403, 409, etc.
                setActionError(err.message || "An unexpected error occurred. Please try again.");
            } finally {
                setIsProcessing(false);
            }
        };

        // --- UPDATED: This now just opens the confirmation modal ---
        const handleRequestQuote = async () => {
            if (gateAction()) return;
            // Guard: Prevent "Request Quote" flow for items marked as "Price on Request"
            if (canRequestPrice) {
                setActionError("Pricing for this item is available upon request only. Please use the 'Request Price' button to inquire.");
                return;
            }
            setShowQuoteConfirmModal(true);
        };

        // --- NEW: This function handles the logic after the user confirms their email ---
        const confirmAndSendQuote = async (recipientEmail) => {
            // This function is passed to the QuoteConfirmationModal
            // It will be called with the email address the user confirmed.

            setActionError(null);
            setActionSuccess(null);
            setActionLoading('Generating quote document...');

            try {
                // 1. Generate the PDF on the client-side with optimizations
                const { jsPDF } = window.jspdf;
                const doc = new jsPDF();
                
                // Optimized image loading with timeout and error handling
                const loadImageWithTimeout = (src, timeout = 5000) => {
                    return new Promise((resolve, reject) => {
                        const img = new Image();
                        const timer = setTimeout(() => {
                            console.warn(`Image load timeout: ${src}`);
                            resolve(null); // Resolve with null on timeout
                        }, timeout);
                        
                        img.onload = () => {
                            clearTimeout(timer);
                            resolve(img);
                        };
                        
                        img.onerror = () => {
                            clearTimeout(timer);
                            console.warn(`Image load failed: ${src}`);
                            resolve(null); // Resolve with null on error
                        };
                        
                        img.src = src;
                    });
                };
                
                setActionLoading('Loading assets...');
                const minersMarketLogo = await loadImageWithTimeout('images/logo/MM - Logo.jpeg');
                const whatsappImage = await loadImageWithTimeout('quote_image/WhatsApp Image 2026-04-29 at 14.26.24.jpeg');
                const productImage = await loadImageWithTimeout(product.image_url_1 || product.image_url || null);

                // --- NEW: Create circular logo for contact section ---
                const logoCanvas = document.createElement('canvas');
                const logoSizePx = Math.min(minersMarketLogo.width, minersMarketLogo.height);
                logoCanvas.width = logoSizePx;
                logoCanvas.height = logoSizePx;
                const logoCtx = logoCanvas.getContext('2d');
                logoCtx.beginPath();
                logoCtx.arc(logoSizePx / 2, logoSizePx / 2, logoSizePx / 2, 0, Math.PI * 2, true);
                logoCtx.closePath();
                logoCtx.clip();
                logoCtx.drawImage(minersMarketLogo, 0, 0, logoSizePx, logoSizePx);
                const circularLogoData = logoCanvas.toDataURL('image/png');

                const isRebuildRequest = product.listingType === 'rebuild';
                console.log('[PDF Generation] Starting PDF generation', { isRebuildRequest, listingType: product.listingType });

                // --- PAGE 1: WhatsApp Image Cover Page ---
                if (whatsappImage) {
                    setActionLoading('Adding cover page...');
                    console.log('[PDF Generation] Adding WhatsApp image as first page');
                    
                    // Add the WhatsApp image to fill the first page
                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();
                    const imgAspectRatio = whatsappImage.width / whatsappImage.height;
                    const pageAspectRatio = pageWidth / pageHeight;
                    
                    let imgWidth, imgHeight, imgX, imgY;
                    
                    if (imgAspectRatio > pageAspectRatio) {
                        // Image is wider than page - fit to width
                        imgWidth = pageWidth;
                        imgHeight = pageWidth / imgAspectRatio;
                        imgX = 0;
                        imgY = (pageHeight - imgHeight) / 2;
                    } else {
                        // Image is taller than page - fit to height
                        imgHeight = pageHeight;
                        imgWidth = pageHeight * imgAspectRatio;
                        imgX = (pageWidth - imgWidth) / 2;
                        imgY = 0;
                    }
                    
                    doc.addImage(whatsappImage, 'JPEG', imgX, imgY, imgWidth, imgHeight);
                    
                    // Add page number at bottom
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(150);
                    doc.text('Page 1', pageWidth / 2, pageHeight - 10, { align: 'center' });
                } else {
                    console.warn('[PDF Generation] WhatsApp image not loaded, skipping cover page');
                }

                // --- PAGE 2: Main Content (for both standard and rebuild) ---
                doc.addPage();
                
                if (isRebuildRequest) {
                    setActionLoading('Creating rebuild document...');
                    console.log('[PDF Generation] Taking REBUILD REQUEST path');
                    // --- REBUILD REQUEST DOCUMENT ---
                    doc.addImage(minersMarketLogo, 'JPEG', 15, 15, 30, 30);
                    
                    // Title
                    doc.setFontSize(20);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(33, 33, 33);
                    doc.text('REBUILD CONFIGURATION REQUEST', doc.internal.pageSize.getWidth() / 2, 25, { align: 'center' });
                    
                    // Meta info
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100);
                    doc.text(`Date: ${new Date().toLocaleDateString()}`, doc.internal.pageSize.getWidth() - 15, 35, { align: 'right' });
                    doc.text(`Listing #: ${product.listing_number}`, doc.internal.pageSize.getWidth() - 15, 40, { align: 'right' });

                    let yPos = 55;

                    // Buyer & Seller Info Grid
                    doc.setDrawColor(200);
                    doc.setFillColor(245, 247, 250);
                    doc.rect(15, yPos, 180, 35, 'F');
                    
                    doc.setFontSize(11);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(33);
                    doc.text('Buyer Details (Request From)', 20, yPos + 8);
                    doc.text('Seller Details (Listing Owner)', 110, yPos + 8);
                    
                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(60);
                    
                    // Buyer
                    doc.text(`Name: ${user.name}`, 20, yPos + 16);
                    doc.text(`Email: ${user.email}`, 20, yPos + 22);
                    doc.text(`Phone: ${user.phone_number || 'N/A'}`, 20, yPos + 28);

                    // Seller
                    doc.text(`Name: ${product.sales_contact_name}`, 110, yPos + 16);
                    doc.text(`Email: ${product.sales_contact_email}`, 110, yPos + 22);
                    doc.text(`Phone: ${product.sales_contact_phone}`, 110, yPos + 28);

                    yPos += 45;

                    // Machine Details
                    doc.setFontSize(12);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(33);
                    doc.text('Machine Information', 15, yPos);
                    yPos += 6;

                    const machineData = [
                        ['Product Name', product.name],
                        ['Manufacturer', product.manufacturer || '-'],
                        ['Model', product.model_number || '-'],
                        ['Serial Number', product.serial_number || '-'],
                        ['Base Price (AS-IS)', formatPrice(product.price)]
                    ];

                    doc.autoTable({
                        startY: yPos,
                        head: [['Field', 'Value']],
                        body: machineData,
                        theme: 'striped',
                        headStyles: { fillColor: [41, 128, 185] },
                        styles: { fontSize: 9 },
                        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } }
                    });

                    yPos = doc.lastAutoTable.finalY + 15;

                    // Configuration Table
                    doc.setFontSize(12);
                    doc.setFont('helvetica', 'bold');
                    doc.text('Requested Rebuild Configuration', 15, yPos);
                    yPos += 6;

                    const scopeColumns = ["Component / System", "Current Status (Seller)", "Requested Scope", "Buyer Notes"];
                    const scopeRows = Object.entries(rebuildConfig).map(([component, config]) => {
                        const sDetails = sellerRebuildDetails[component] || {};
                        const currentStatus = sDetails.condition ? `${sDetails.condition}${sDetails.hours ? ` (${sDetails.hours}h)` : ''}` : 'Not Specified';
                        const displayName = (component.startsWith('Other') && sDetails.other_name) ? sDetails.other_name : component;
                        return [displayName, currentStatus, config.condition, config.notes || '-'];
                    });
                    
                    doc.autoTable({
                        startY: yPos,
                        head: [scopeColumns],
                        body: scopeRows,
                        theme: 'grid',
                        headStyles: { fillColor: [52, 73, 94] },
                        styles: { fontSize: 9, cellPadding: 3 },
                        columnStyles: { 0: { fontStyle: 'bold' } }
                    });

                    // Footer / Disclaimer
                    const finalY = doc.lastAutoTable.finalY + 20;
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'italic');
                    doc.setTextColor(100);
                    doc.text("This document is a request for quotation based on the configuration above. It is not a binding contract.", 105, finalY, { align: 'center' });

                    // --- Add Terms and Conditions ---
                    doc.addPage();
                    let termsY = 20;
                    console.log('[PDF Generation] Adding terms and conditions page (rebuild)');

                    // Header
                    doc.setFontSize(12);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(33, 33, 33);
                    doc.text('Terms and Conditions:', 15, termsY);
                    doc.setLineWidth(0.5);
                    doc.setDrawColor(200, 200, 200);
                    doc.line(15, termsY + 2, 195, termsY + 2);
                    termsY += 10;
                    
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(60, 60, 60);

                    const addTermSection = (title, content) => {
                        doc.setFont('helvetica', 'bold');
                        doc.text(title, 15, termsY);
                        termsY += 5;
                        doc.setFont('helvetica', 'normal');
                        const lines = doc.splitTextToSize(content, 180);
                        doc.text(lines, 15, termsY);
                        termsY += (lines.length * 4) + 4;
                    };

                    addTermSection('Quotation Currency', 'THIS QUOTATION IS CALCULATED IN SOUTHERN AFRICAN CURRENCY (ZAR)');
                    addTermSection('Pricing', '• Prices quoted include VAT.\n• Any optional accessories, add-ons, or customisations requested by the customer will be quoted separately and may incur additional charges.');
                    addTermSection('Validity', '• This quotation is valid for five (5) days from the date of issue.\n• Prices may be subject to change after the validity period expires.');
                    addTermSection('Payment Terms', '• Accepted payment methods:\n   o Bank Transfer (EFT)\n   o Credit Card\n   o Cheque\n   (Trade Safe Payment Options Available via Miners Market)\n• Full payment is required before the machine/equipment will be released.');
                    addTermSection('Delivery', '• Delivery arrangements, costs, and timelines will be discussed and confirmed with the Miners Market team upon POP confirmation. Inspection of equipment/spares can be arranged on request.');
                    addTermSection('General', '• This quotation is subject to Miners Market\'s standard terms and conditions.\n• Availability of equipment is subject to prior sale.');

                    termsY += 5;
                    doc.setFont('helvetica', 'italic');
                    const closingText = doc.splitTextToSize('We look forward to doing business with you and building a long-standing working relationship with you and your company.', 180);
                    doc.text(closingText, 15, termsY);
                    termsY += (closingText.length * 4) + 10;

                    // --- Contact Section ---
                    if (termsY > 240) { doc.addPage(); termsY = 20; }
                    doc.setFillColor(248, 249, 250);
                    doc.setDrawColor(220, 220, 220);
                    doc.rect(15, termsY, 180, 45, 'FD');
                    
                    const footerLogoSize = 25;
                    const footerLogoX = 20;
                    const footerLogoY = termsY + 10;
                    doc.addImage(circularLogoData, 'PNG', footerLogoX, footerLogoY, footerLogoSize, footerLogoSize);
                    doc.setDrawColor(200, 200, 200);
                    doc.setLineWidth(0.5);
                    doc.circle(footerLogoX + footerLogoSize / 2, footerLogoY + footerLogoSize / 2, footerLogoSize / 2, 'S');

                    let contactY = termsY + 8;
                    const col1 = 50; const col2 = 115;
                    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(33, 33, 33);
                    doc.text('The Miners Market Team', col1, contactY);
                    contactY += 8; doc.setFontSize(8); doc.setFont('helvetica', 'normal');
                    doc.text('Phone: +27 82 455 1658', col1, contactY); contactY += 5;
                    doc.text('Email: info@minersmarket.co.za (General Enquiries)', col1, contactY); contactY += 5;
                    doc.text('Email: finance@minersmarket.co.za (Finance)', col1, contactY); contactY += 5;
                    let socialX = col1; doc.text('Social: ', socialX, contactY); socialX += doc.getTextWidth('Social: ');
                    doc.setTextColor(0, 0, 255); doc.text('LinkedIn', socialX, contactY, { url: 'https://www.linkedin.com/company/miners-market/posts/?feedView=all' }); socialX += doc.getTextWidth('LinkedIn');
                    doc.setTextColor(33, 33, 33); doc.text(' | ', socialX, contactY); socialX += doc.getTextWidth(' | ');
                    doc.setTextColor(0, 0, 255); doc.text('Facebook', socialX, contactY, { url: 'https://www.facebook.com/people/Miners-Market/61581241269225/' }); socialX += doc.getTextWidth('Facebook');
                    doc.setTextColor(33, 33, 33); doc.text(' | ', socialX, contactY); socialX += doc.getTextWidth(' | ');
                    doc.setTextColor(0, 0, 255); doc.text('WhatsApp', socialX, contactY, { url: 'https://whatsapp.com/channel/0029VbAgvIQHAdNVnXCaN30z' });
                    doc.setTextColor(33, 33, 33);
                    contactY = termsY + 16;
                    doc.text('Web: www.minersmarket.co.za', col2, contactY, { url: 'https://www.minersmarket.co.za' }); contactY += 5;
                    doc.text('Email: quotes@minersmarket.co.za (Quotations)', col2, contactY); contactY += 5;
                    doc.text('Email: support@minersmarket.co.za (Technical)', col2, contactY);

                } else {
                    console.log('[PDF Generation] Taking STANDARD QUOTATION path');
                    // --- STANDARD QUOTATION DOCUMENT ---
                    doc.addImage(minersMarketLogo, 'JPEG', 15, 15, 30, 30);
                    doc.setFontSize(22);
                    doc.setFont('helvetica', 'bold');
                    doc.text('OFFICIAL QUOTATION', doc.internal.pageSize.getWidth() / 2, 25, { align: 'center' });
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100);
                    
                    doc.text(`Quote for: ${user.name}`, 15, 50);
                    doc.text(`Date: ${new Date().toLocaleDateString()}`, doc.internal.pageSize.getWidth() - 15, 50, { align: 'right' });

                    const price = parseFloat(product.price) || 0;
                    const vat = price * 0.15;
                    const total = price + vat;
                    const tableColumn = ["Description", "Listing #", "Unit Price", "VAT (15%)", "Total"];
                    const tableRows = [[product.name, product.listing_number, formatPrice(price), formatPrice(vat), formatPrice(total)]];
                    doc.autoTable({
                        head: [tableColumn],
                        body: tableRows,
                        startY: 60,
                        headStyles: { fillColor: [22, 160, 133] },
                        styles: { textColor: [33, 33, 33] },
                    });
                    const finalY = doc.autoTable.previous.finalY;

                    // Add product image if available
                    if (productImage) {
                        const imgSize = 50;
                        const imgX = 15;
                        const requiredHeight = imgSize + 20; // Image + caption + padding
                        
                        let imgY = finalY + 10;
                        
                        // Add new page if not enough space for image
                        if (imgY + requiredHeight > 280) {
                            doc.addPage();
                            imgY = 20;
                        }
                        
                        doc.addImage(productImage, 'JPEG', imgX, imgY, imgSize, imgSize);
                        
                        // Add image caption
                        doc.setFontSize(8);
                        doc.setFont('helvetica', 'italic');
                        doc.setTextColor(100);
                        doc.text('Product Image', imgX, imgY + imgSize + 5);
                    }

                if (isRebuildRequest) {
                    setActionLoading('Sending rebuild request...');
                    // Send to Seller (Submit Rebuild Request)
                    const response = await fetch('/api/submit_rebuild_request.php', {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            seller_email: product.sales_contact_email,
                            seller_name: product.sales_contact_name,
                            seller_phone: product.sales_contact_phone, // Include phone for admin
                            product_name: product.name,
                            listing_number: product.listing_number,
                            listing_id: product.id,
                            seller_id: product.seller_id,
                            rebuild_config: rebuildConfig, // Send the form data to DB
                            pdf_data: pdfData
                        })
                    });
                    const result = await response.json();
                    if (!response.ok) throw new Error(result.error || 'Failed to submit rebuild request.');
                    setActionSuccess('Rebuild request sent to seller successfully.');
                } else {
                    // Send to Buyer (Standard Quote) - using async endpoint for instant response
                    setActionLoading('Processing quote request...');
                    const emailResponse = await fetch('/api/send_quote_email_async.php', {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            recipient_email: recipientEmail,
                            recipient_name: user.name,
                            product_id: product.id,
                            listing_type: product.listingType,
                            product_name: product.name,
                            listing_number: product.listing_number,
                            pdf_data: pdfData
                        })
                    });
                    const emailResult = await emailResponse.json();
                    if (!emailResponse.ok) throw new Error(emailResult.error || 'Failed to send quote email.');
                    setActionSuccess('Your quote has been sent to ' + recipientEmail);
                }

                // --- LAST PAGE: Banking Details and Terms & Conditions ---
                doc.addPage();
                let lastPageY = 20;
                console.log('[PDF Generation] Adding banking details and terms as last page');

                // Banking Details Section
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(33, 33, 33);
                doc.text('Banking Details:', 15, lastPageY);
                console.log('[PDF Generation] Adding banking details');
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                
                // Standard banking details for all quotes
                doc.text('Bank Name: FIRST NATIONAL BANK', 15, lastPageY + 7);
                doc.text('Account Type: GOLD BUSINESS ACCOUNT', 15, lastPageY + 14);
                doc.text('Account Number: 63204739205', 15, lastPageY + 21);
                doc.text('Branch Code: 258155', 15, lastPageY + 28);
                doc.text('Branch Name: FNB POP BRANCH DOLYMPUS', 15, lastPageY + 35);
                doc.text('SWIFT Code: FIRNZAJJ', 15, lastPageY + 42);
                doc.setFont('helvetica', 'bold');
                doc.text('Payment Reference: Please use listing number ' + product.listing_number, 15, lastPageY + 49);

                // Terms and Conditions Section
                lastPageY += 65;
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(33, 33, 33);
                doc.text('Terms and Conditions:', 15, lastPageY);
                // Add T&C link note
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(7);
                doc.setTextColor(100, 100, 100);
                doc.text("PLEASE USE LINK TO VIEW", 195, lastPageY, { align: 'right' });

                doc.setLineWidth(0.5);
                doc.setDrawColor(200, 200, 200);
                doc.line(15, lastPageY + 2, 195, lastPageY + 2);
                lastPageY += 10;
                
                doc.setFontSize(8); // Use a smaller font for terms
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(60, 60, 60);

                const addTermSection = (title, content) => {
                    doc.setFont('helvetica', 'bold');
                    doc.text(title, 15, lastPageY);
                    lastPageY += 5;
                    doc.setFont('helvetica', 'normal');
                    const lines = doc.splitTextToSize(content, 180);
                    doc.text(lines, 15, lastPageY);
                    lastPageY += (lines.length * 4) + 4; // 4mm per line + gap
                };

                addTermSection('Quotation Currency', 'THIS QUOTATION IS CALCULATED IN SOUTHERN AFRICAN CURRENCY (ZAR)');
                
                addTermSection('Pricing', 
                    '• Prices quoted include VAT.\n' +
                    '• Any optional accessories, add-ons, or customisations requested by customer will be quoted separately and may incur additional charges.'
                );

                addTermSection('Validity', 
                    '• This quotation is valid for five (5) days from date of issue.\n' +
                    '• Prices may be subject to change after the validity period expires.'
                );

                addTermSection('Payment Terms', 
                    '• Accepted payment methods:\n' +
                    '   o Bank Transfer (EFT)\n' +
                    '   o Credit Card\n' +
                    '   o Cheque\n' +
                    '   (Trade Safe Payment Options Available via Miners Market)\n' +
                    '• Full payment is required before machine/equipment will be released.'
                );

                addTermSection('Delivery', 
                    '• Delivery arrangements, costs, and timelines will be discussed and confirmed with Miners Market team upon POP confirmation. Inspection of equipment/spares can be arranged on request.'
                );

                addTermSection('General', 
                    '• This quotation is subject to Miners Market\'s standard terms and conditions.\n' +
                    '• Availability of equipment is subject to prior sale.'
                );

                lastPageY += 5;
                doc.setFont('helvetica', 'italic');
                const closingText = doc.splitTextToSize('We look forward to doing business with you and building a long-standing working relationship with you and your company.', 180);
                doc.text(closingText, 15, lastPageY);
                lastPageY += (closingText.length * 4) + 10;

                // Contact Section
                // Ensure we don't run off the page
                if (lastPageY > 240) {
                    doc.addPage();
                    lastPageY = 20;
                }
                console.log('[PDF Generation] Adding contact section');

                // Draw a box for contact info
                doc.setFillColor(248, 249, 250); // Light gray
                doc.setDrawColor(220, 220, 220);
                doc.rect(15, lastPageY, 180, 45, 'FD'); // Filled and Stroked
                
                // Add Circular Logo to Contact Section (Left Side)
                const footerLogoSize = 25;
                const footerLogoX = 20; // Moved to left
                const footerLogoY = lastPageY + 10;
                doc.addImage(circularLogoData, 'PNG', footerLogoX, footerLogoY, footerLogoSize, footerLogoSize);
                doc.setDrawColor(200, 200, 200);
                doc.setLineWidth(0.5);
                doc.circle(footerLogoX + footerLogoSize / 2, footerLogoY + footerLogoSize / 2, footerLogoSize / 2, 'S');

                let contactY = lastPageY + 8;
                const col1 = 50; // Shifted right to accommodate logo on left
                const col2 = 115; // Shifted right

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(33, 33, 33);
                doc.text('The Miners Market Team', col1, contactY);
                
                contactY += 8;
                doc.setFontSize(8); // Reduced font size
                doc.setFont('helvetica', 'normal');
                
                // Column 1
                doc.text('Phone: +27 82 455 1658', col1, contactY);
                contactY += 5;
                doc.text('Email: info@minersmarket.co.za (General Enquiries)', col1, contactY);
                contactY += 5;
                doc.text('Email: finance@minersmarket.co.za (Finance)', col1, contactY);
                contactY += 5;
                
                // Social Links Line
                let socialX = col1;
                doc.text('Social: ', socialX, contactY);
                socialX += doc.getTextWidth('Social: ');
                
                doc.setTextColor(0, 0, 255); // Blue for links
                doc.text('LinkedIn', socialX, contactY, { url: 'https://www.linkedin.com/company/miners-market/posts/?feedView=all' });
                socialX += doc.getTextWidth('LinkedIn');
                
                doc.setTextColor(33, 33, 33); // Reset color
                doc.text(' | ', socialX, contactY);
                socialX += doc.getTextWidth(' | ');
                
                doc.setTextColor(0, 0, 255);
                doc.text('Facebook', socialX, contactY, { url: 'https://www.facebook.com/people/Miners-Market/61581241269225/' });
                socialX += doc.getTextWidth('Facebook');
                
                doc.setTextColor(33, 33, 33);
                doc.text(' | ', socialX, contactY);
                socialX += doc.getTextWidth(' | ');
                
                doc.setTextColor(0, 0, 255);
                doc.text('WhatsApp', socialX, contactY, { url: 'https://whatsapp.com/channel/0029VbAgvIQHAdNVnXCaN30z' });
                
                doc.setTextColor(33, 33, 33); // Reset for next lines

                // Column 2 (reset Y)
                contactY = lastPageY + 16; // Align with first phone number line roughly
                doc.text('Web: www.minersmarket.co.za', col2, contactY, { url: 'https://www.minersmarket.co.za' });
                contactY += 5;
                doc.text('Email: quotes@minersmarket.co.za (Quotations)', col2, contactY);
                contactY += 5;
                doc.text('Email: support@minersmarket.co.za (Technical)', col2, contactY);

                // Add page number at bottom
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(150);
                const totalPages = doc.internal.getNumberOfPages();
                doc.text(`Page ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

                setActionLoading('Finalizing document...');
                const pdfData = doc.output('datauristring').split(',')[1];
                console.log('[PDF Generation] PDF data generated, length:', pdfData.length, 'bytes');

                if (isRebuildRequest) {
                    setActionLoading('Sending rebuild request...');
                    // Send to Seller (Submit Rebuild Request)
                    const response = await fetch('/api/submit_rebuild_request.php', {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            seller_email: product.sales_contact_email,
                            seller_name: product.sales_contact_name,
                            seller_phone: product.sales_contact_phone, // Include phone for admin
                            product_name: product.name,
                            listing_number: product.listing_number,
                            listing_id: product.id,
                            seller_id: product.seller_id,
                            rebuild_config: rebuildConfig, // Send form data to DB
                            pdf_data: pdfData
                        })
                    });
                    const result = await response.json();
                    if (!response.ok) throw new Error(result.error || 'Failed to submit rebuild request.');
                    setActionSuccess('Rebuild request sent to seller successfully.');
                } else {
                    // Send to Buyer (Standard Quote) - using async endpoint for instant response
                    setActionLoading('Processing quote request...');
                    const emailResponse = await fetch('/api/send_quote_email_async.php', {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            recipient_email: recipientEmail,
                            recipient_name: user.name,
                            product_id: product.id,
                            listing_type: product.listingType,
                            product_name: product.name,
                            listing_number: product.listing_number,
                            pdf_data: pdfData
                        })
                    });
                    const emailResult = await emailResponse.json();
                    if (!emailResponse.ok) throw new Error(emailResult.error || 'Failed to send quote email.');
                    setActionSuccess('Your quote has been sent to ' + recipientEmail);
                }
                
                setQuoteRequested(true); // Mark as requested for UI state
                setShowQuoteConfirmModal(false); // Close the confirmation modal on success
            }
            } catch (err) {
                setActionError(err.message);
                throw err; // Re-throw to be caught by the confirmation modal
            }
        };

        // --- NEW: Function to generate Rebuild PDF ---
        const generateRebuildPdf = async () => {
            try {
                const { jsPDF } = window.jspdf;
                const doc = new jsPDF();

                // Helper to load images
                const loadImage = (src) => new Promise((resolve) => {
                    const img = new Image();
                    img.src = src;
                    img.onload = () => resolve(img);
                    img.onerror = () => {
                        console.warn(`Failed to load image: ${src}`);
                        resolve(null);
                    };
                });

                // --- NEW: Load User-Supplied Main Images ---
                const userImageUrls = [product.image_url_1, product.image_url_2, product.image_url_3, product.image_url_4].map(cleanPath).filter(Boolean);
                const mainImages = await Promise.all(userImageUrls.map(loadImage));

                // --- NEW: Load Matrix Component Images ---
                const matrixImageMap = {};
                const matrixPromises = [];
                Object.entries(sellerRebuildDetails).forEach(([comp, details]) => {
                    const urls = (details.images || (details.image_url ? [details.image_url] : [])).map(cleanPath).filter(Boolean);
                    if (urls.length > 0) {
                        matrixPromises.push(Promise.all(urls.map(loadImage)).then(imgs => {
                            matrixImageMap[comp] = imgs.filter(Boolean);
                        }));
                    }
                });
                await Promise.all(matrixPromises);

                // Load all assets (Logo + 5 parts)
                const [minersMarketLogo, part1, part2, part3, part4, part6] = await Promise.all([
                    loadImage('images/logo/MM - Logo.jpeg'),
                    loadImage('images/equipmentrebuildpdf/part1.png'),
                    loadImage('images/equipmentrebuildpdf/part2.png'),
                    loadImage('images/equipmentrebuildpdf/part3.png'),
                    loadImage('images/equipmentrebuildpdf/part4.png'),
                    loadImage('images/equipmentrebuildpdf/part6.png')
                ]);

                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                const addFullPage = (img) => {
                    if (img) doc.addImage(img, 'PNG', 0, 0, pageWidth, pageHeight);
                };

                // --- Part 1 ---
                addFullPage(part1);

                // --- Part 2 ---
                doc.addPage();
                addFullPage(part2);

                // --- Part 3 ---
                doc.addPage();
                addFullPage(part3);

                // --- Part 4 ---
                doc.addPage();
                addFullPage(part4);

                // --- Part 5: Dynamic Content Start ---
                doc.addPage();
                
                // Add Logo
                if (minersMarketLogo) {
                    try {
                        doc.addImage(minersMarketLogo, 'JPEG', 15, 15, 30, 30);
                    } catch (e) {
                        console.warn("Logo add failed", e);
                    }
                }

                doc.setFontSize(20);
                doc.setFont('helvetica', 'bold');
                doc.text('Rebuild Status Inspection', 105, 25, { align: 'center' });
                
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                doc.text(`Listing #: ${product.listing_number}`, 105, 32, { align: 'center' });
                doc.text(`Date: ${new Date().toLocaleDateString()}`, 195, 25, { align: 'right' });

                let yPos = 55;

                // Product Info
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('Equipment Details', 15, yPos);
                yPos += 5;
                
                const infoData = [
                    ['Item Name', product.name],
                    ['Manufacturer', product.manufacturer],
                    ['Model', product.model_number],
                    ['Serial Number', product.serial_number],
                    ['Description', product.description],
                    ['Year', product.year?.toString() || 'N/A'],
                    ['Hours', `${product.hours?.toString() || 'N/A'} (N/A, LIFE of EQUIPMENT)`],
                    ['Location', product.location]
                ];

                doc.autoTable({
                    startY: yPos,
                    head: [['Field', 'Value']],
                    body: infoData,
                    theme: 'striped',
                    headStyles: { fillColor: [41, 128, 185] },
                    styles: { fontSize: 10 },
                    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } }
                });

                yPos = doc.lastAutoTable.finalY + 15;

                // --- NEW: Insert Main Product Images ---
                const validMainImages = mainImages.filter(Boolean);
                if (validMainImages.length > 0) {
                    // Check for space
                    if (yPos + 50 > pageHeight - 20) { doc.addPage(); yPos = 20; }

                    doc.setFontSize(12);
                    doc.setFont('helvetica', 'bold');
                    doc.text('Equipment Photos', 15, yPos);
                    yPos += 7;

                    let imgX = 15;
                    validMainImages.forEach((img) => {
                        if (imgX + 40 > pageWidth - 15) return; // Prevent overflow
                        try {
                            // Let jsPDF auto-detect the image type (PNG, JPEG, or WEBP)
                            doc.addImage(img, imgX, yPos, 40, 40);
                            imgX += 45;
                        } catch (e) { console.warn("Image add error", e); }
                    });
                    yPos += 45; // Advance Y position past images
                }

                // Rebuild Matrix
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('Component Status Inspection', 15, yPos);
                yPos += 5;

                // Helper to get column config per category
                const getCategoryConfig = (category) => {
                    const v = (val) => (val === undefined || val === null || val === '') ? '-' : val;
                   switch (category) {
                        case "Powertrain & Drivetrain":
                            return {
                                headers: ['Component', 'Condition', 'Runner', 'Hours', 'Description / Notes', 'Images'],
                                map: (d) => [v(d.condition), v(d.runner), d.hours_na ? 'N/A' : v(d.hours), v(d.comment)]
                            };
                        case "Hydraulic, Fuel & Fluid Systems":
                            return {
                                headers: ['Component', 'Condition', 'System Status', 'Description / Notes', 'Images'],
                                map: (d) => [v(d.condition), v(d.system_status), v(d.comment)]
                            };
                        case "Electrical & Electronic Systems":
                            return {
                                headers: ['Component', 'Installed', 'Operational Status', 'Description / Notes', 'Images'],
                                map: (d) => [v(d.installed), v(d.operational_status), v(d.comment)]
                            };
                        case "Structure, Chassis & Frame":
                            return {
                                headers: ['Component', 'Condition', 'Damage Type', 'Description / Notes', 'Images'],
                                map: (d) => {
                                    const damageTypes = d.damage_types ? Object.keys(d.damage_types).filter(k => d.damage_types[k]).join(',\n') : (d.damage_notes || '-');
                                    return [v(d.condition), damageTypes, v(d.comment)];
                                }
                            };
                        case "Safety Systems":
                            return {
                                headers: ['Component', 'Installed', 'Operational', 'Compliance', 'Description / Notes', 'Images'],
                                map: (d) => [v(d.installed), v(d.operational), v(d.compliance_status), v(d.comment)]
                            };
                        case "Wear Components & Running Gear":
                            return {
                                headers: ['Component', 'Condition', 'Wear Level', 'Description / Notes', 'Images'],
                                map: (d) => [v(d.condition), v(d.wear_level), v(d.comment)]
                            };
                        case "Bodywork, Cab & Visibility":
                            return {
                                headers: ['Component', 'Condition', 'Corrosion', 'Description / Notes', 'Images'],
                                map: (d, item) => {
                                    if (item === 'Windows' && d.cab_type === 'open') {
                                        return ['Open Cab', '-', '-', ''];
                                    }
                                    return [v(d.condition), v(d.corrosion_status), v(d.comment)];
                                }
                            };
                        case "Compliance, Legal & Identification":
                            return {
                                headers: ['Component', 'Present', 'Legible', 'Compliance', 'Description / Notes', 'Images'],
                                map: (d) => [v(d.present), v(d.legible), v(d.compliance_status), v(d.comment)]
                            };
                        default:
                            return {
                                headers: ['Component', 'Condition', 'Description / Notes', 'Images'],
                                map: (d) => [v(d.condition), v(d.comment)]
                            };
                    }
                };

                let hasAnyData = false;

                for (const [category, items] of Object.entries(REBUILD_MATRIX_DATA)) {
                    const activeItems = items.filter(item => sellerRebuildDetails[item]);


                    if (activeItems.length === 0) continue;
                    hasAnyData = true;

                    const config = getCategoryConfig(category);

                    // Prepare rows
                    const rows = activeItems.map(item => {
                        const details = sellerRebuildDetails[item];
                        const itemName = (item.startsWith('Other ') && details.other_name) ? details.other_name : item.replace(/ \(.*/, '');

                        if (details.not_applicable) {
                            const emptyValues = new Array(config.headers.length - 2).fill('-');
                            emptyValues[0] = 'Not Applicable';
                            return [itemName, ...emptyValues, ''];
                        }

                        let comment = details.comment || '-';
                        if (details.additional_info) comment += `\nNote: ${details.additional_info}`;
                        
                        const dProxy = { ...details, comment };
                        const values = config.map(dProxy, item);
                        
                        return [itemName, ...values, '']; // Append empty for images column
                    });

                    // Check for page break
                    if (yPos + 40 > pageHeight) {
                        doc.addPage();
                        yPos = 20;
                    }

                    // Draw Section Header with Background Color
                    doc.setFillColor(230, 230, 230); // Light gray background
                    doc.rect(15, yPos, 180, 8, 'F');
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(0);
                    doc.text(category.toUpperCase(), 18, yPos + 5.5);
                    yPos += 8;

                    doc.autoTable({
                        startY: yPos,
                        head: [config.headers],
                        body: rows,
                        theme: 'grid',
                        headStyles: { fillColor: [44, 62, 80], fontSize: 8 },
                        styles: { fontSize: 8, cellPadding: 2, minCellHeight: 25, valign: 'middle' },
                        columnStyles: {
                            0: { cellWidth: 40, fontStyle: 'bold' },
                            [config.headers.length - 1]: { cellWidth: 45 } // Image column is always last
                        },
                        didDrawCell: (data) => {
                            // Render images in the last column
                            if (data.section === 'body' && data.column.index === config.headers.length - 1) {
                                const compName = activeItems[data.row.index]; // Map row index back to item key
                                const imgs = matrixImageMap[compName];
                                if (imgs && imgs.length > 0) {
                                    let x = data.cell.x + 2;
                                    let y = data.cell.y + 2;
                                    const size = 20; // Reasonable thumbnail size
                                    imgs.forEach(img => {
                                        if (x + size < data.cell.x + data.cell.width) {
                                            try { 
                                                // Let jsPDF auto-detect the image type (PNG, JPEG, or WEBP)
                                                doc.addImage(img, x, y, size, size);
                                                x += size + 2;
                                            } catch (e) {
                                                console.warn("Failed to add matrix image to PDF", e);
                                            }
                                        }
                                    });
                                }
                            }
                        }
                    });
                    yPos = doc.lastAutoTable.finalY + 10;
                }

                if (!hasAnyData) {
                    doc.setFont('helvetica', 'italic');
                    doc.setFontSize(10);
                    doc.text('No specific rebuild details declared (Sold AS-IS).', 15, yPos + 10);
                    yPos += 15;
                }

                // --- NEW: Add Inspection & Service Section ---
                const inspectionServiceItems = [
                    "Full Service", "Operational Testing", "Safety Audit", "Final Quality Sign-Off",
                    "Good clean runner", "Rebuild to zero base", "Spares only/Service exchange", "As-is", "Custom requirement"
                ];
                const inspectionRows = [];
                inspectionServiceItems.forEach(item => {
                    const details = sellerRebuildDetails[item];
                    if (details) {
                        let value = details.selected === 'yes' ? 'Yes' : (details.selected === 'no' ? 'No' : '-');
                        if (item === "Operational Testing" && details.comment) {
                            value += `\n(${details.comment})`;
                        }
                        inspectionRows.push([item, value]);
                    }
                });

                if (inspectionRows.length > 0) {
                    if (yPos + 20 > pageHeight) { doc.addPage(); yPos = 20; }
                    doc.autoTable({
                        startY: yPos + 10,
                        head: [['Final Inspection & Condition', 'Status']],
                        body: inspectionRows,
                        theme: 'striped',
                        headStyles: { fillColor: [41, 128, 185] },
                    });
                }

                // --- Part 6: Final Page ---
                doc.addPage();
                addFullPage(part6);

                // Footer (Only on dynamic content pages: 5 to N-1)
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 5; i < pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.setTextColor(150);
                    doc.text(`Generated by Miners Market - Page ${i - 4} of ${pageCount - 5}`, 105, 287, { align: 'center' });
                }

                doc.save(`Rebuild_Status_${product.listing_number}.pdf`);

            } catch (err) {
                console.error("PDF Generation Error:", err);
              alert("Failed to generate PDF summary.");
            }
        };

        const handleTradeSafePurchase = async () => {
            setIsProcessing(true);
            setActionError(null);

            if (gateAction()) {
                setIsProcessing(false);
                return;
            }

            if (!user.name || !user.email || !user.phone_number) {
                setActionError("Your profile is incomplete. Please add your phone number in your profile to proceed.");
                setIsProcessing(false);
                return;
            }
            // Use the correct field names from the database schema for validation
            if (!product.sales_contact_name || !product.sales_contact_email || !product.sales_contact_phone) {
                setActionError("Seller contact information is missing for this product. Cannot proceed.");
                setIsProcessing(false);
                return;
            }

            try {
                // The total amount to be paid is the item price + platform fee.
                const totalAmountToPay = totalAmount;
                const body = {
                    product_title: product.name,
                    product_desc: product.description || 'No description provided.',
                    amount: totalAmountToPay,
                    product_id: product.id,
                    buyer_id: user.id,
                    seller_id: product.seller_id,
                    listingType: product.listingType, // Add the listing type to the payload
                    quantity: selectedQuantity, // Send the quantity the user selected
                    buyer_name: user.name,
                    buyer_email: user.email,
                    buyer_phone: user.phone_number,
                    seller_name: product.sales_contact_name,
                    seller_email: product.sales_contact_email,
                    seller_phone: product.sales_contact_phone
                };

                const res = await fetch('/api/initiate_payment.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                });

                const result = await res.json();

                if (!res.ok) throw new Error(result.error || 'Failed to initiate payment.');
                if (!result.checkoutUrl) throw new Error('Checkout URL not received from server.');

                window.location.href = result.checkoutUrl; // Redirect to TradeSafe

            } catch (err) {
                setActionError(err.message);
                setIsProcessing(false);
            }
        };

        const handleInquiry = () => {
            if (gateAction()) return;
            // This will open our custom modal for inquiries on rentals/services
            setShowInquiryModal(true);
        };

        const handleDieselQuote = () => {
            if (gateAction()) return;
            // This will open the diesel quote modal for diesel products
            setShowDieselQuoteModal(true);
        };

        const sendDieselQuote = async (formData) => {
            setIsProcessing(true);
            setActionError(null);
            setActionSuccess(null);
            try {
                const response = await fetch('/api/send_diesel_quote.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        ...formData, 
                        product_name: product.name,
                        listing_number: product.listing_number
                    })
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Failed to send diesel quote.');
                
                setActionSuccess(result.message);
                setShowDieselQuoteModal(false); // Close diesel quote modal on success
            } catch (err) {
                setActionError(err.message);
            } finally {
                setIsProcessing(false);
            }
        };

        const sendInquiry = async (message) => {
            setIsProcessing(true);
            setActionError(null);
            setActionSuccess(null);
            try {
                const response = await fetch('/api/send_inquiry_email.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ product, message })
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Failed to send inquiry.');
                
                setActionSuccess(result.message);
                setShowInquiryModal(false); // Close inquiry modal on success
            } catch (err) {
                setActionError(err.message);
            } finally {
                setIsProcessing(false);
            }
        };

        // --- NEW: Handlers for Size Selection ---
        const handleSizeQtyChange = (size, delta) => {
            setSelectedSizes(prev => {
                const currentQty = prev[size] || 0;
                const maxQty = availableSizes[size] || 0;
                const newQty = currentQty + delta;

                if (newQty < 0) return prev;
                if (newQty > maxQty) return prev;

                const next = { ...prev };
                if (newQty === 0) delete next[size];
                else next[size] = newQty;
                return next;
            });
        };

        // --- NEW: Handler for Rebuild Matrix Selection ---
        const handleRebuildSelection = (component, type, value) => {
            setRebuildConfig(prev => {
                const current = prev[component] || { condition: '', notes: '' };
                
                if (type === 'condition') {
                    if (!value) { // Unchecked / Removed
                        const next = { ...prev };
                        delete next[component];
                        return next;
                    }
                    return { ...prev, [component]: { ...current, condition: value } };
                }
                if (type === 'notes') {
                    return { ...prev, [component]: { ...current, notes: value } };
                }
                return prev;
            });
        };

        const handleSaveBlurredImage = async (imageData, originalUrl) => {
            // This function is passed to the ImageBlurEditor
            setIsProcessing(true);
            setActionError(null);
            setActionSuccess(null);

            // Find the sort_order for this image from the listing_media table
            let sortOrder = null;
            
            // First check if product has media array (new structure)
            if (product.media && Array.isArray(product.media)) {
                const mediaItem = product.media.find(m => 
                    m.media_type === 'image' && 
                    cleanPath(m.file_path) === cleanPath(originalUrl)
                );
                if (mediaItem && mediaItem.sort_order !== undefined) {
                    sortOrder = mediaItem.sort_order;
                }
            }
            
            // Fallback to old image_url_X structure
            if (sortOrder === null) {
                for (let i = 1; i <= 10; i++) {
                    if (product[`image_url_${i}`] && cleanPath(product[`image_url_${i}`]) === cleanPath(originalUrl)) {
                        sortOrder = i;
                        break;
                    }
                }
            }

            if (sortOrder === null) {
                const err = new Error("Could not identify the image to update. Please refresh and try again.");
                setActionError(err.message);
                setIsProcessing(false);
                throw err; // Also throw to the editor
            }

            try {
                const payload = {
                    imageData,
                    productId: product.id,
                    listingType: product.listingType,
                    imageColumn: `image_url_${sortOrder}`
                };

                const response = await fetch('/api/save_blurred_image.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Failed to save blurred image.');
                
                setActionSuccess(result.message);
                setImageToEdit(null); // Close the blur editor
                // Don't call onProductUpdate here to prevent modal from closing
                // The product modal will stay open for continued editing
                // Refresh the product data to show the updated blurred image
                if (onProductUpdate) {
                    onProductUpdate();
                }
            } catch (err) {
                // Set the error to be displayed inside the editor component
                // Don't throw - let the editor handle the error display
                setActionError(err.message || "Failed to save blurred image.");
            } finally {
                setIsProcessing(false);
            }
        };

        const handleAddWatermark = async (imageUrl) => {
            if (!imageUrl) {
                setActionError('Please select an image to watermark.');
                return;
            }

            setIsWatermarkProcessing(true);
            setActionError(null);
            setActionSuccess(null);

            // Find the sort_order for this image from the listing_media table
            let sortOrder = null;
            
            // First check if product has media array (new structure)
            if (product.media && Array.isArray(product.media)) {
                const mediaItem = product.media.find(m => 
                    m.media_type === 'image' && 
                    cleanPath(m.file_path) === cleanPath(imageUrl)
                );
                if (mediaItem && mediaItem.sort_order !== undefined) {
                    sortOrder = mediaItem.sort_order;
                }
            }
            
            // Fallback to old image_url_X structure
            if (sortOrder === null) {
                for (let i = 1; i <= 10; i++) {
                    if (product[`image_url_${i}`] && cleanPath(product[`image_url_${i}`]) === cleanPath(imageUrl)) {
                        sortOrder = i;
                        break;
                    }
                }
            }

            if (sortOrder === null) {
                const err = new Error("Could not identify the image to update. Please refresh and try again.");
                setActionError(err.message);
                setIsWatermarkProcessing(false);
                return;
            }

            try {
                const payload = {
                    productId: product.id,
                    listingType: product.listingType,
                    imageColumn: `image_url_${sortOrder}`
                };

                console.log('Sending watermark request:', payload);

                const response = await fetch('/api/add_watermark.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                console.log('Watermark response status:', response.status);
                
                let result;
                try {
                    result = await response.json();
                    console.log('Watermark response data:', result);
                } catch (e) {
                    console.error('Failed to parse JSON response:', e);
                    const text = await response.text();
                    console.error('Response text:', text);
                    throw new Error('Invalid response from server');
                }
                
                if (!response.ok) throw new Error(result.error || 'Failed to add watermark.');
                
                setActionSuccess(result.message);
                
                // Force image refresh by adding timestamp to the URL
                const timestamp = new Date().getTime();
                const updatedImageUrl = imageUrl.includes('?') 
                    ? `${imageUrl}&t=${timestamp}` 
                    : `${imageUrl}?t=${timestamp}`;
                
                console.log('Updated image URL:', updatedImageUrl);
                
                // Update the main image if it's the one being watermarked
                if (mainImage === imageUrl) {
                    setMainImage(updatedImageUrl);
                }
                
                // Force the browser to reload the image immediately
                const imgElements = document.querySelectorAll(`img[src*="${imageUrl}"]`);
                console.log('Found image elements to update:', imgElements.length);
                imgElements.forEach(img => {
                    img.src = updatedImageUrl;
                });
                
                // Also update any images that might have the clean path
                const cleanImgPath = cleanPath(imageUrl);
                const allImages = document.querySelectorAll('img');
                allImages.forEach(img => {
                    if (cleanPath(img.src) === cleanImgPath) {
                        img.src = updatedImageUrl;
                    }
                });
                
                // Keep the modal open so user can see the updated image
                // Don't close it immediately
                setTimeout(() => {
                    setShowWatermarkSelector(false);
                    setSelectedImageForWatermark(null);
                }, 2000);
                
                // Refresh the product data to show the updated watermarked image
                if (onProductUpdate) {
                    onProductUpdate();
                }
            } catch (err) {
                console.error('Watermark error:', err);
                setActionError(err.message || "Failed to add watermark.");
            } finally {
                setIsWatermarkProcessing(false);
            }
        };
    
        // The price from the database is the final unit price.
        const finalUnitPrice = parseFloat(product.price) || 0;
        
        // --- UPDATED: Calculate total based on simple quantity OR complex size selection ---
        const complexTotalQty = availableSizes ? Object.values(selectedSizes).reduce((a, b) => a + b, 0) : 0;
        const effectiveQuantity = availableSizes ? complexTotalQty : selectedQuantity;
        const totalAmount = finalUnitPrice * effectiveQuantity;

        // For the breakdown display, calculate the fee and seller's portion from the total amount.
        // Use a globally available fee percentage instead of hardcoding.
        const platformFee = totalAmount * (window.APP_CONFIG?.platformFeePercentage || 0.06);
        const itemPrice = totalAmount - platformFee; // This is the seller's payout portion.

        const handleContactAdmin = () => {
            if (gateAction()) return;
            // This will now open our custom modal
            setShowContactModal(true);
        };

        // Helper to render a detail item only if it has a value
        const DetailItem = ({ label, value, formatAsDescription = false }) => {
            if (!value) return null;
            return (
                <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-500 mb-1">{label}</h4>
                    {formatAsDescription ? (
                        <div className="text-gray-800 break-words">{formatDescription(value)}</div>
                    ) : (
                        <p className="text-gray-800 break-words">{value}</p>
                    )}
                </div>
            );
        };

        // Determine the correct button text and action for inquiry-based items
        let inquiryButtonText = 'Inquire Now';
        if (product.listingType === 'rental') {
            inquiryButtonText = 'I want to Rent';
        } else if (product.listingType === 'service') {
            inquiryButtonText = 'Inquire about Service';
        }

        // --- REFINED LOGIC FOR ACTION BUTTONS ---
        // Determines if the item can be purchased directly via TradeSafe.
        const isPurchasable = ['standard', 'wots', 'consumable', 'mine', 'ore', 'conveyor', 'filter', 'grease', 'kg_pps'].includes(product.listingType);
        // Determines if the item is inquiry-based (rentals, services).
        const isInquiryBased = ['rental', 'service'].includes(product.listingType);
        // Determines if the item is a rebuild candidate
        const isRebuild = product.listingType === 'rebuild';
        // Determines if the item is diesel (requires special quote form)
        const isDiesel = product.listingType === 'diesel' || product.name.toLowerCase().includes('diesel');
        // Determines if the "Request Price" feature is applicable and active for this item.
        const canRequestPrice = ['standard', 'wots', 'conveyor', 'filter', 'grease', 'kg_pps', 'service'].includes(product.listingType) && (product.is_price_on_request || !product.price || parseFloat(product.price) <= 0);

        // Debug logging for diesel detection
        if (isDiesel) {
            console.log('[ProductModal] Diesel product detected:', {
                name: product.name,
                listingType: product.listingType,
                isDiesel: isDiesel,
                price: product.price,
                is_price_on_request: product.is_price_on_request
            });
        }

        const kpLink = '/kp-insurance';

        return (
            <React.Fragment>
            <div data-name="product-modal" data-file="components/ProductModal.js"
                 className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4"
                 onClick={(e) => {
                     // Never close on backdrop click - user must use X button
                     // This prevents accidental closures
                 }}
            >
                <div className="bg-white rounded-xl w-full max-w-lg md:max-w-2xl lg:max-w-4xl max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="flex justify-between items-start p-4 border-b">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{product.name}</h2>
                            {product.bearing_type && <p className="text-md text-blue-600 font-semibold">{product.bearing_type}</p>}
                            <p className="text-sm text-gray-500 font-mono mt-1">Listing #: {product.listing_number}</p>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><i className="fas fa-times text-xl"></i></button>
                    </div>

                    {/* Main Content */}
                    <div className="flex-grow overflow-y-auto p-4 md:p-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Image Gallery */}
                            {actionError && (
                                <div className="md:col-span-2 bg-red-100 text-red-700 p-3 rounded-lg text-sm mb-4">{actionError}</div>
                            )}
                            {actionLoading && (
                                <div className="md:col-span-2 bg-blue-100 text-blue-700 p-3 rounded-lg text-sm mb-4 flex items-center">
                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                    {actionLoading}
                                </div>
                            )}
                            <div>
                                <div 
                                    className="relative mb-4"
                                    onTouchStart={handleTouchStart}
                                    onTouchEnd={handleTouchEnd}
                                >
                                    <img src={optimizeImageSrc(mainImage || 'images/logo/MM - Logo.jpeg', 1200)} alt={product.name} className="w-full h-80 object-contain bg-gray-50 rounded-lg shadow-md" />
                                    {productImages.length > 1 && (
                                        <>
                                            <button onClick={(e) => handleImageNav(e, -1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/50 transition-colors z-10">
                                                <i className="fas fa-chevron-left text-lg"></i>
                                            </button>
                                            <button onClick={(e) => handleImageNav(e, 1)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/50 transition-colors z-10">
                                                <i className="fas fa-chevron-right text-lg"></i>
                                            </button>
                                        </>
                                    )}
                                    {/* KG Logo for Bearings */}
                                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('KG') && (
                                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG Bearings" className="w-12 h-auto object-contain" />
                                         </div>
                                    )}
                                    {/* FAG Logo for Bearings */}
                                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('FAG') && (
                                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('/Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/FAG.png', 120)} alt="FAG Bearings" className="w-12 h-auto object-contain" />
                                         </div>
                                    )}
                                    {/* NTN Logo for Bearings */}
                                    {product.bearing_type && product.brand && product.brand.toUpperCase().includes('NTN') && (
                                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('/Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/NTN.png', 120)} alt="NTN Bearings" className="w-12 h-auto object-contain" />
                                         </div>
                                    )}
                                    {/* KG Logo for Filters */}
                                    {product.listingType === 'filter' && (
                                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('/Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/KG-Filter-high-res-logo blue.png', 200)} alt="KG Filters" className="w-24 h-auto object-contain" />
                                         </div>
                                    )}
                                    {/* ARB Logo for Conveyor Belts */}
                                    {product.listingType === 'conveyor' && (
                                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('/miners-market-main/Miscellaneous Items/Logos-20260206T070056Z-3-001/Logos/arb-logo.png', 160)} alt="ARB" className="w-20 h-auto object-contain" />
                                         </div>
                                    )}
                                    {/* KG Logo for Greases */}
                                    {product.listingType === 'grease' && (
                                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG Ultra" className="w-12 h-auto object-contain" />
                                         </div>
                                    )}
                                    {/* KG Logo for PPS */}
                                    {product.listingType === 'kg_pps' && (
                                         <div className="absolute top-2 left-2 bg-white p-1 rounded-md shadow-sm">
                                            <img src={optimizeImageSrc('images/logo/kg-bearings-logo.webp', 120)} alt="KG ULTRA" className="w-12 h-auto object-contain" />
                                         </div>
                                    )}
                                    {/* Badge logic for the modal */}
                                    {(() => {
                                        const badgeSrc = { // This is now consistent with ProductCard
                                            'wots': 'images/badges/MM Badge.png',
                                            'standard': 'images/badges/MM Badge.png',
                                        };
                                        // The badgeType is now passed in with the product object from the parent page
                                        return product.badgeType && badgeSrc[product.badgeType] && (
                                            <img 
                                                src={optimizeImageSrc(badgeSrc[product.badgeType], 100)} 
                                                alt={`${product.badgeType} listing badge`}
                                                className="absolute top-4 right-4 w-20 h-20 object-contain" 
                                            />
                                        );
                                    })()}
                                {/* --- NEW: Share Icon --- */}
                                <button
                                    onClick={handleShare}
                                    className="absolute bottom-4 left-4 w-10 h-10 rounded-full bg-white/30 backdrop-blur-md text-gray-900 border border-white/20 flex items-center justify-center transition-all hover:bg-white/50 z-20 shadow-lg"
                                    title="Share Listing"
                                >
                                    <i className="fas fa-share-alt text-lg"></i>
                                </button>
                                    {productImages.length > 1 && (
                                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5 z-10">
                                            {productImages.slice(0, 20).map((_, index) => (
                                                <div key={index} className={`w-2 h-2 rounded-full transition-colors ${currentImageIndex === index ? 'bg-white' : 'bg-white/50'}`}></div>
                                            ))}
                                        </div>
                                    )}
                                    {/* --- NEW: Edit button directly on the main image for admins --- */}
                                    {user && user.activeRole === 'admin' && (
                                        <button
                                            onClick={() => setImageToEdit(mainImage)}
                                            className="absolute top-2 left-20 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all"
                                            title="Edit/Blur Image"
                                        >
                                            <i className="fas fa-magic text-lg"></i>
                                        </button>
                                    )}
                                </div>
                                {productImages.length > 1 && ( // Added overflow-x-auto and padding-bottom
                                    <div className="flex space-x-2 overflow-x-auto pb-2">
                                        {productImages.map((img, index) => (                                            
                                            <div key={img + index} className="relative group flex-shrink-0">
                                                <img // Added flex-shrink-0
                                                    src={optimizeImageSrc(img, 300)} 
                                                    alt={`Thumbnail ${index + 1}`}
                                                    onClick={() => {
                                                        setMainImage(img);
                                                        setCurrentImageIndex(index);
                                                    }}
                                                    className={`w-16 h-16 object-cover rounded-md cursor-pointer border-2 ${currentImageIndex === index ? 'border-blue-500' : 'border-transparent'}`}
                                                />
                                                {user && user.activeRole === 'admin' && (
                                                    <button 
                                                        onClick={() => setImageToEdit(img)}
                                                        className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-md"
                                                        title="Edit/Blur Image">
                                                        <i className="fas fa-magic text-lg"></i>
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {/* --- NEW: Watermark button for admins --- */}
                                {user && user.activeRole === 'admin' && productImages.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                        <button
                                            onClick={() => setShowWatermarkSelector(true)}
                                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center justify-center"
                                        >
                                            <i className="fas fa-tint mr-2"></i>
                                            Add Watermark to Selected Images
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Product Info */}
                            <div className="min-w-0">
                                <div className="flex justify-between items-center mb-4">
                                    {canRequestPrice ? (
                                        <div className="w-full flex flex-col items-end gap-2">
                                            <button 
                                                onClick={handleRequestPrice} 
                                                disabled={isProcessing || priceRequested}
                                                className="primary-gradient text-white font-bold py-2 px-6 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                            >
                                                {isProcessing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Requesting...</> : (priceRequested ? 'Price Requested' : 'Request Price')}
                                            </button>
                                            {priceRequested && (
                                                <p className="text-xs text-gray-500 text-right">As soon as price has been accepted please check your Buyer Dashboard</p>
                                            )}
                                        </div>
                                    ) : (product.is_price_on_request && (product.price === null || product.listingType === 'service')) ? (
                                        <div className="w-full flex justify-end">
                                            <p className="text-3xl font-bold text-blue-600">POR</p>
                                        </div>
                                    ) : (
                                        <div className="min-w-0">
                                            <p className="text-3xl font-bold text-blue-600 truncate">
                                                {formatPrice(totalAmount)}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {effectiveQuantity === 0 ? 'Select items' : (effectiveQuantity > 1 ? `Total for ${effectiveQuantity} items` : (product.listingType === 'rebuild' ? 'Base Price (Before Work Done)' : 'Total Price (incl. fee)'))}
                                            </p>
                                        </div>
                                    )}
                                    
                                    {/* Simple Quantity Selector (Only if NOT availableSizes AND NOT Bearing) */}
                                    {!availableSizes && !product.bearing_type && product.quantity > 1 && (
                                        <div className="text-right">
                                            <label htmlFor="quantity-selector" className="block text-sm font-medium text-gray-700">Quantity</label>
                                            <input
                                                id="quantity-selector"
                                                type="number"
                                                value={selectedQuantity}
                                                onChange={(e) => {
                                                    let val = parseInt(e.target.value, 10);
                                                    if (isNaN(val) || val < 1) val = 1;
                                                    if (val > product.quantity) val = product.quantity;
                                                    setSelectedQuantity(val);
                                                }}
                                                min="1"
                                                max={product.quantity}
                                                className="mt-1 w-20 text-center border-gray-300 rounded-md shadow-sm"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">{product.quantity} available</p>
                                        </div>
                                    )}
                                    {!(product.is_price_on_request && (product.price === null || product.listingType === 'service')) && (
                                        <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800 ml-2">{product.category_name}</span>
                                    )}
                                </div>

                                {/* --- UPDATED: Complex Quantity Selector Logic (Moved out of flex container) --- */}
                                {availableSizes && (
                                    <div className="w-full mb-6 bg-gray-50 p-3 md:p-4 rounded-lg border border-gray-200">
                                        <h4 className="text-sm font-bold text-gray-700 mb-3">Select Sizes & Quantity</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                                            {Object.entries(availableSizes).map(([size, stock]) => (
                                                <div key={size} className="flex flex-col bg-white p-2 rounded border shadow-sm">
                                                    <div className="flex justify-between text-xs mb-1">
                                                        <span className="font-bold truncate mr-1" title={size}>{size}</span>
                                                        <span className="text-gray-500 truncate">{stock} left</span>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <button onClick={() => handleSizeQtyChange(size, -1)} className="w-6 h-6 bg-gray-200 rounded hover:bg-gray-300 flex items-center justify-center text-gray-700" disabled={!selectedSizes[size]}>-</button>
                                                        <span className="font-semibold text-sm">{selectedSizes[size] || 0}</span>
                                                        <button onClick={() => handleSizeQtyChange(size, 1)} className="w-6 h-6 bg-blue-100 rounded hover:bg-blue-200 flex items-center justify-center text-blue-700" disabled={(selectedSizes[size] || 0) >= stock}>+</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-3 text-right text-sm">
                                            Total Items: <span className="font-bold">{complexTotalQty}</span>
                                        </div>
                                    </div>
                                )}

                                        {/* --- NEW: Rebuild Classification Display --- */}
                                        {product.listingType === 'rebuild' && product.rebuild_classification && (() => {
                                            const classification = REBUILD_CLASSIFICATIONS.find(c => c.id === product.rebuild_classification);
                                            if (!classification) return null;
                                            return (
                                                <div className={`p-4 mb-4 rounded-lg border ${classification.badgeClasses} flex items-start gap-4`}>
                                                    <i className={`${classification.icon} text-2xl mt-1`}></i>
                                                    <div>
                                                        <h4 className={`font-bold text-lg ${classification.titleColor}`}>{classification.title}</h4>
                                                        <p className={`text-sm ${classification.descColor}`}>{classification.desc}</p>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* --- NEW: KG ULTRA Banner for Bearings --- */}
                                        {product.bearing_type && (
                                            <div className="bg-blue-900 text-white p-3 rounded-lg mb-4 flex items-center justify-between shadow-md">
                                                <div>
                                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-200 block">Brand</span>
                                                    <span className="text-lg font-bold">KG ULTRA</span>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* --- NEW: KG Banner for Filters --- */}
                                        {product.listingType === 'filter' && (
                                            <div className="bg-blue-900 text-white p-3 rounded-lg mb-4 flex items-center justify-between shadow-md">
                                                <div>
                                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-200 block">Brand</span>
                                                    <span className="text-lg font-bold">KG Filters</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* --- NEW: ARB Banner for Conveyor Belts --- */}
                                        {product.listingType === 'conveyor' && (
                                            <div className="bg-blue-900 text-white p-3 rounded-lg mb-4 flex items-center justify-between shadow-md">
                                                <div>
                                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-200 block">Brand</span>
                                                    <span className="text-lg font-bold">ARB PLUS</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* --- NEW: KG Banner for Greases --- */}
                                        {product.listingType === 'grease' && (
                                            <div className="bg-blue-900 text-white p-3 rounded-lg mb-4 flex items-center justify-between shadow-md">
                                                <div>
                                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-200 block">Brand</span>
                                                    <span className="text-lg font-bold">KG Ultra</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* --- NEW: KG Banner for PPS --- */}
                                        {product.listingType === 'kg_pps' && (
                                            <div className="bg-blue-900 text-white p-3 rounded-lg mb-4 flex items-center justify-between shadow-md">
                                                <div>
                                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-200 block">Brand</span>
                                                    <span className="text-lg font-bold">KG ULTRA</span>
                                                </div>
                                            </div>
                                        )}

                                {/* Description - Use rich description for bearings if available, else standard description */}
                                {product.bearing_type ? getBearingRichDescription(product.bearing_type) : (
                                    <div className="mb-6">{formatDescription(product.description)}</div>
                                )}

                                {/* Tabs */}
                                <div className="border-b border-gray-200 mb-4">
                                    <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-2">
                                        <button onClick={() => setActiveTab('details')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'details' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Details</button>
                                        <button onClick={() => setActiveTab('contact')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'contact' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Seller Info</button>
                                        {isRebuild && (
                                            <>
                                                <button onClick={() => setActiveTab('rebuild_status')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'rebuild_status' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>Rebuild Status</button>
                                            </>
                                        )}
                                    </nav>
                                </div>

                                {/* Tab Content */}
                                {activeTab === 'details' && (
                                    <div className="space-y-4 animate-fade-in">
                                        {/* Standard Product Details */}
                                        {(product.listingType === 'standard' || product.listingType === 'wots' || product.listingType === 'rebuild') && (
                                            <>
                                                <DetailItem label="Condition" value={product.item_condition} />
                                                <DetailItem label="Manufacturer" value={product.manufacturer} />
                                                <DetailItem label="Model Number" value={product.model_number} />
                                                <DetailItem label="Part Number" value={product.part_number} />
                                                <DetailItem label="Serial Number" value={product.serial_number} />
                                                <DetailItem label="Application" value={product.application} />
                                                <DetailItem label="Quantity Available" value={product.quantity} />
                                                {!product.bearing_type && <DetailItem label="Quantity Available" value={product.quantity} />}
                                                <DetailItem label="Location" value={product.location} />
                                                <DetailItem label="Shipping Terms" value={product.shipping_terms} />
                                                <DetailItem label="Packaging" value={product.packaging} />
                                                <DetailItem label="Estimated Delivery" value={product.delivery_time} />
                                                {product.specifications && <div className="bg-gray-50 p-3 rounded-lg">
                                                    <h4 className="text-sm font-semibold text-gray-500 mb-1">Specifications</h4>
                                                    <p className="text-gray-800 whitespace-pre-wrap">{product.specifications}</p>
                                                </div>}
                                                {product.compatibility && <div className="bg-gray-50 p-3 rounded-lg">
                                                    <h4 className="text-sm font-semibold text-gray-500 mb-1">Compatibility</h4>
                                                    <p className="text-gray-800 whitespace-pre-wrap">{product.compatibility}</p>
                                                </div>}
                                            </>
                                        )}
                                        {/* Rental Details */}
                                        {product.listingType === 'rental' && (
                                            <>
                                                <DetailItem label="Equipment Type" value={product.equipment_type} />
                                                <DetailItem label="Size" value={product.size} />
                                                <DetailItem label="Rate per Hour" value={formatPrice(product.rate_per_hour || product.price)} />
                                                <DetailItem label="Minimum Hire" value={product.min_hire_duration} />
                                            </>
                                        )}
                                        {/* Consumable Details */}
                                        {product.listingType === 'consumable' && (
                                            <>
                                                <DetailItem label="Description" value={product.description} formatAsDescription={true} />
                                                <DetailItem label="Brand" value={product.brand} />
                                                <DetailItem label="Size / Quantity" value={availableSizes ? "Please select sizes above" : product.size} />
                                            </>
                                        )}
                                        {/* Ore Sale Details */}
                                        {product.listingType === 'ore' && (
                                            <>
                                                <DetailItem label="Description" value={product.description} formatAsDescription={true} />
                                                <DetailItem label="Ore Type" value={product.category_name} />
                                                <DetailItem label="Grade" value={product.ore_grade} />
                                                <DetailItem label="Quantity Available" value={product.quantity_available} />
                                                <DetailItem label="Location" value={product.location} />
                                            </>
                                        )}
                                        {/* Mine Sale Details */}
                                        {product.listingType === 'mine' && (
                                            <>
                                                <DetailItem label="Description" value={product.description} formatAsDescription={true} />
                                                <DetailItem label="Location" value={product.location} />
                                            </>
                                        )}
                                        {/* Service Details */}
                                        {product.listingType === 'service' && (
                                            <>
                                                <DetailItem label="Service Offered" value={product.service_name || product.name} />
                                                <DetailItem label="Service Description" value={product.service_description || product.description} formatAsDescription={true} />
                                                <DetailItem label="Rate per Hour" value={product.is_price_on_request ? 'Price on Request' : formatPrice(product.rate_per_hour || product.price)} />
                                            </>
                                        )}
                                        {/* Grease Details */}
                                        {product.listingType === 'grease' && (
                                            <>
                                                <DetailItem label="Product Code" value={product.part_number} />
                                                <DetailItem label="Full Description" value={product.full_description} formatAsDescription={true} />
                                                <DetailItem label="Application" value={product.application} />
                                                <DetailItem label="Features" value={product.features} />
                                                <DetailItem label="Temperature Range" value={product.temperature_range} />
                                                <DetailItem label="Shelf Life" value={product.shelf_life} />
                                                <DetailItem label="Storage Conditions" value={product.storage_conditions} />
                                                {product.datasheet_url && (
                                                <div className="mt-4">
                                                    <a 
                                                        href={product.datasheet_url} 
                                                        download
                                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                    >
                                                        <i className="fas fa-file-download mr-2"></i> Download Datasheet
                                                    </a>
                                                </div>
                                                )}
                                            </>
                                        )}
                                        {/* KG PPS Details */}
                                        {product.listingType === 'kg_pps' && product.datasheet_url && (
                                            <div className="mt-4">
                                                <a 
                                                    href={product.datasheet_url} 
                                                    download
                                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                >
                                                    <i className="fas fa-file-download mr-2"></i> Download Datasheet
                                                </a>
                                            </div>
                                        )}
                                        {/* Component Checklist Display */}
                                        {checklistItems && (
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <h4 className="text-sm font-semibold text-gray-500 mb-2">Included Components</h4>
                                                <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                                                    {checklistItems.map(item => (
                                                        <li key={item} className="flex items-center text-sm text-gray-800">
                                                            <i className="fas fa-check-circle text-green-500 mr-2"></i>
                                                            <span>{item}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* --- NEW: Technical Specifications (Inline) --- */}
                                {(product.bearing_type || product.specs || product.listingType === 'conveyor' || product.listingType === 'filter' || product.listingType === 'grease' || product.listingType === 'kg_pps') && (
                                    <div className="mt-6 pt-6 border-t border-gray-200">
                                        <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Technical Specifications</h4>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                            {(() => {
                                                const data = { ...product, ...(product.specs || {}) };
                                                const internalKeys = ['id', 'product_id', 'seller_id', 'category_id', 'created_at', 'updated_at', 'status', 'listing_type', 'listing_market', 'image_url_1', 'image_url_2', 'image_url_3', 'image_url_4', 'image_url_5', 'image_url_6', 'image_url_7', 'image_url_8', 'image_url_9', 'image_url_10', 'image_url_11', 'image_url_12', 'image_url_13', 'image_url_14', 'image_url_15', 'image_url_16', 'image_url_17', 'image_url_18', 'image_url_19', 'image_url_20', 'image_url_21', 'image_url_22', 'image_url_23', 'image_url_24', 'image_url_25', 'slug', 'name', 'description', 'price', 'original_price', 'quantity', 'is_price_on_request', 'listing_number', 'sales_contact_name', 'sales_contact_phone', 'sales_contact_email', 'location', 'seller_name', 'avg_rating', 'rating_count', 'category_name', 'badgeType', 'specs', 'image_url', 'type_description', 'media', 'datasheet_url', 'full_description', 'short_description', 'applications', 'features', 'technical_specs', 'temperature_range', 'shelf_life', 'storage_conditions', 'grease_type', 'filter_type', 'brand', 'manufacturer', 'model_number', 'part_number', 'serial_number', 'year', 'hours', 'rebuild_classification', 'rebuild_details', 'willing_to_sell_spares', 'application', 'origin', 'packaging', 'rate_per_hour', 'min_hire_duration', 'size', 'equipment_type', 'service_name', 'service_description', 'ore_grade', 'quantity_available', 'specifications', 'compatibility', 'shipping_terms', 'delivery_time', 'company_name_address', 'support_contact_details'];
                                                
                                                return Object.entries(data)
                                                    .filter(([key, value]) => !internalKeys.includes(key) && value && value !== 'NA' && value !== '0.00' && (typeof value === 'string' || typeof value === 'number'))
                                                    .map(([key, value]) => (
                                                        <div key={key} className="bg-gray-50 p-2 rounded border border-gray-100">
                                                            <span className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">{key.replace(/_/g, ' ')}</span>
                                                            <span className="block text-sm text-gray-800 font-medium break-words">{value}</span>
                                                        </div>
                                                    ));
                                            })()}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'contact' && (
                                    <div className="space-y-4">
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <h4 className="text-sm font-semibold text-gray-500 mb-1">Seller Rating</h4>
                                            <StarRating rating={product.avg_rating || 0} count={product.rating_count || 0} />
                                        </div>
                                        {/* Admins see full contact details for verification and support */}
                                        {user && user.activeRole === 'admin' && (
                                            <>
                                                <DetailItem label="Sales Contact" value={product.sales_contact_name} />
                                                <DetailItem label="Phone" value={product.sales_contact_phone} />
                                                <DetailItem label="Email" value={product.sales_contact_email} />
                                                <DetailItem label="Company & Address" value={product.company_name_address} />
                                                {product.support_contact_details && <div className="bg-gray-50 p-3 rounded-lg">
                                                    <h4 className="text-sm font-semibold text-gray-500 mb-1">Support Details</h4>
                                                    <p className="text-gray-800 whitespace-pre-wrap">{product.support_contact_details}</p>
                                                </div>}
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* --- NEW: Rebuild Status Tab (Seller Declaration) --- */}
                                {activeTab === 'rebuild_status' && isRebuild && (
                                    <div className="space-y-6 animate-fade-in">
                                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                            <div>
                                                <h4 className="font-bold text-gray-800 mb-1">Current Machine Status</h4>
                                                <p className="text-sm text-gray-600">This is the current condition of the machine's components as declared by the seller.</p>
                                            </div>
                                            <button 
                                                onClick={generateRebuildPdf}
                                                className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center shadow-sm w-full sm:w-auto justify-center"
                                            >
                                                <i className="fas fa-file-pdf text-red-500 mr-2"></i> Download PDF
                                            </button>
                                        </div>
                                        
                                        {product.willing_to_sell_spares === 'yes' && (
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
                                                <i className="fas fa-recycle text-green-600 text-xl mr-3 mt-1"></i>
                                                <div>
                                                    <h4 className="font-bold text-green-800 text-sm">Spares & Components Available</h4>
                                                    <p className="text-sm text-green-700 mt-1">The seller has indicated they are willing to sell individual parts or components from this machine (stripping for spares). Please contact the seller or admin to inquire about specific parts.</p>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {Object.entries(REBUILD_MATRIX_DATA).map(([category, items]) => {
                                            // Filter items that have data in sellerRebuildDetails
                                            const activeItems = items.filter(item => sellerRebuildDetails[item]);
                                            if (activeItems.length === 0) return null;

                                            return (
                                                <div key={category} className="border rounded-lg overflow-hidden">
                                                    <div className="bg-gray-100 px-4 py-2 font-bold text-gray-800">{category}</div>
                                                    <div className="p-4 space-y-4">
                                                        {activeItems.map(item => {
                                                            const details = sellerRebuildDetails[item];
                                                            if (details.not_applicable) {
                                                                return (
                                                                    <div key={item} className="border-b last:border-0 pb-4 last:pb-0">
                                                                        <div className="flex justify-between items-start">
                                                                            <p className="font-bold text-gray-400 line-through">{details.other_name || item.replace(/ \(.*/, '')}</p>
                                                                            <span className="text-sm font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded-md">Not Applicable</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }
                                            if (item === 'Windows' && details.cab_type === 'open') {
                                                return (
                                                    <div key={item} className="border-b last:border-0 pb-4 last:pb-0">
                                                        <div className="flex justify-between items-start">
                                                            <p className="font-bold text-gray-900">{item.replace(/ \(.*/, '')}</p>
                                                            <span className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded-md">Open Cab</span>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                                            return (
                                                                <div key={item} className="border-b last:border-0 pb-4 last:pb-0">
                                                                    <div className="flex justify-between items-start">
                                                                        <div className="flex-grow pr-4">
                                                                            <p className="font-bold text-gray-900">{details.other_name || item.replace(/ \(.*/, '')}</p>
                                                                            <div className="text-sm text-gray-600 mt-2 space-y-1">
                                                                                <DetailDisplay label="Condition" value={details.condition} />
                                                                                <DetailDisplay label="Runner" value={details.runner} />
                                                                                <DetailDisplay label="Hours" value={details.hours_na ? 'N/A' : details.hours} />
                                                                                <DetailDisplay label="System Status" value={details.system_status} />
                                                                                <DetailDisplay label="Installed" value={details.installed} />
                                                                                <DetailDisplay label="Operational Status" value={details.operational_status} />
                                                                                <DetailDisplay label="Damage Type" value={details.damage_types ? Object.keys(details.damage_types).filter(k => details.damage_types[k]).join(', ') : details.damage_notes} />
                                                                                <DetailDisplay label="Operational" value={details.operational} />
                                                                                <DetailDisplay label="Compliance" value={details.compliance_status} />
                                                                                <DetailDisplay label="Wear Level" value={details.wear_level} />
                                                                                <DetailDisplay label="Corrosion" value={details.corrosion_status} />
                                                                                <DetailDisplay label="Present" value={details.present} />
                                                                                <DetailDisplay label="Legible" value={details.legible} />
                                                                                
                                                                                {details.comment && details.comment !== 'N/A' && <p className="italic mt-1">"{details.comment}"</p>}
                                                                                {details.additional_info && <p className="text-xs text-gray-500 mt-1">Note: {details.additional_info}</p>}
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {(details.images || (details.image_url ? [details.image_url] : [])).map((url, idx) => (
                                                                                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                                                                                    <img src={url} alt={`${item} ${idx + 1}`} className="w-16 h-16 object-cover rounded border hover:opacity-75 transition-opacity" />
                                                                                </a>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {Object.keys(sellerRebuildDetails).length === 0 && (
                                            <p className="text-gray-500 text-center py-4">No specific rebuild details declared by the seller (Sold AS-IS).</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Documents Section */}
                        {validDocuments.length > 0 && (
                            <div className="mt-6 pt-6 border-t">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Attached Documents</h3>
                                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                                    {validDocuments.map(doc => (
                                        <a 
                                            key={doc.name} 
                                            href={doc.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="bg-gray-100 p-3 rounded-lg flex items-center space-x-3 hover:bg-gray-200 transition-colors"
                                        >
                                            <i className="fas fa-file-alt text-gray-500 text-xl"></i>
                                            <span className="text-sm font-medium text-gray-800">{doc.name}</span>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer / Actions */}
                    {(!user || (user && user.activeRole === 'buyer')) && isPurchasable && (
                        <div className="p-6 border-t bg-gray-50 rounded-b-xl transition-all duration-300">                            {canRequestPrice ? (
                                <div className="flex flex-wrap justify-end gap-4">
                                    <button onClick={handleContactAdmin} className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors"><i className="fas fa-envelope mr-2"></i>Contact Admin</button>
                                </div>
                            ) : (
                                <>
                                {showPriceBreakdown ? (
                                    <div className="animate-fade-in">
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 space-y-3 text-sm">
                                            <div className="flex justify-between"><span>{product.listingType === 'rebuild' ? 'Base Item Price:' : 'Item Price:'}</span><span className="font-medium">{formatPrice(itemPrice)}</span></div>
                                            <div className="flex justify-between"><span>Platform Fee:</span><span className="font-medium">{formatPrice(platformFee)}</span></div>
                                            <div className="flex justify-between font-bold text-base border-t pt-2 mt-2"><span>{product.listingType === 'rebuild' ? 'Total Base Amount: [VAT EXCL]' : 'Total Amount: [VAT EXCL]'}</span><span>{formatPrice(totalAmount)}</span></div>
                                        </div>
                                        <div className="flex flex-wrap justify-end gap-4">
                                            <button onClick={() => setShowPriceBreakdown(false)} className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors">Back</button>
                                            <button 
                                                onClick={handleTradeSafePurchase}
                                                disabled={isProcessing}
                                                className="flex-1 primary-gradient text-white py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isProcessing 
                                                    ? <><i className="fas fa-spinner fa-spin mr-2"></i>Connecting...</>
                                                    : <>Confirm & Pay</>
                                                }
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap justify-end gap-4">
                                        <button onClick={handleContactAdmin} className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors"><i className="fas fa-envelope mr-2"></i>Contact Admin</button>
                                    </div>
                                )}
                                </>
                            )}
                        </div>
                    )}

                    {/* --- NEW: Diesel Quote Actions --- */}
                    {(!user || (user && user.activeRole === 'buyer')) && isDiesel && (
                        <div className="p-6 border-t bg-gray-50 rounded-b-xl flex flex-col sm:flex-row gap-4">
                            {actionLoading && (
                                <div className="text-blue-600 text-sm font-medium text-center animate-fade-in flex items-center justify-center">
                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                    {actionLoading}
                                </div>
                            )}
                            {actionSuccess && (
                                <div className="text-green-600 text-sm font-medium text-center animate-fade-in">
                                    {actionSuccess}
                                </div>
                            )}
                            <button onClick={handleContactAdmin} className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors">
                                <i className="fas fa-envelope mr-2"></i>Contact Admin
                            </button>
                            <button
                                onClick={handleDieselQuote}
                                disabled={isProcessing}
                                className="flex-1 primary-gradient text-white py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                            >
                                {isProcessing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Processing...</> : <><i className="fas fa-gas-pump mr-2"></i>Request Diesel Quote</>}
                            </button>
                        </div>
                    )}

                    {/* --- NEW: Rental / Service Actions --- */}
                    {(!user || (user && user.activeRole === 'buyer')) && isInquiryBased && (
                        <div className="p-6 border-t bg-gray-50 rounded-b-xl flex flex-col sm:flex-row gap-4">
                            <button onClick={handleContactAdmin} className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors">
                                <i className="fas fa-envelope mr-2"></i>Contact Admin
                            </button>
                            <button
                                onClick={handleRequestQuote}
                                disabled={isProcessing || quoteRequested}
                                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-opacity disabled:opacity-50"
                            >
                                {isProcessing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Processing...</> : 
                                    (quoteRequested ? <><i className="fas fa-check mr-2"></i>Quote Sent</> : <><i className="fas fa-file-invoice-dollar mr-2"></i>Request Quote</>)
                                }
                            </button>
                        </div>
                    )}

                    {/* --- UPDATED: Action buttons for different listing types --- */}
                    {(!user || (user && user.activeRole === 'buyer')) && isPurchasable && !canRequestPrice && product.listingType === 'consumable' && (
                        <div className="p-6 border-t bg-gray-50 rounded-b-xl flex flex-col sm:flex-row gap-4">
                            {actionLoading && (
                                <div className="text-blue-600 text-sm font-medium text-center animate-fade-in flex items-center justify-center">
                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                    {actionLoading}
                                </div>
                            )}
                            {actionSuccess && (
                                <div className="text-green-600 text-sm font-medium text-center animate-fade-in">
                                    {actionSuccess}
                                </div>
                            )}
                            <button 
                                onClick={() => {
                                    onAddToBasket(product, effectiveQuantity, selectedSizes, false); // Pass false to prevent global success message
                                    setItemAdded(true);
                                    setActionSuccess(`${product.name} added to basket!`);
                                    // Auto-close modal after showing success message
                                    setTimeout(() => {
                                        setActionSuccess(null);
                                        onClose();
                                    }, 1500);
                                }}
                                disabled={isProcessing || itemAdded || (availableSizes && complexTotalQty === 0)}
                                className="w-full primary-gradient text-white py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {itemAdded ? <><i className="fas fa-check-circle mr-2"></i>Added to Basket</> : <><i className="fas fa-shopping-basket mr-2"></i>Add to Basket</>}
                            </button>
                        </div>
                    )}

                    {(!user || (user && user.activeRole === 'buyer')) && (isPurchasable || isRebuild) && !canRequestPrice && product.listingType !== 'consumable' && !isDiesel && (
                        <div className="p-6 border-t bg-gray-50 rounded-b-xl flex flex-col sm:flex-row gap-4">
                            <button onClick={handleContactAdmin} className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition-colors">
                                <i className="fas fa-envelope mr-2"></i>Contact Admin
                            </button>
                            <button
                                onClick={handleRequestQuote}
                                disabled={isProcessing || quoteRequested}
                                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-opacity disabled:opacity-50"
                            >
                                {isProcessing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Processing...</> : 
                                    (quoteRequested ? <><i className="fas fa-redo-alt mr-2"></i>Request Quote Again</> : <><i className="fas fa-file-invoice-dollar mr-2"></i>Request a Quote</>)
                                }
                            </button>
                        </div>
                    )}

                    {/* --- NEW: Confirmation Modal for Quotes --- */}
                    {showQuoteConfirmModal && (
                        <QuoteConfirmationModal
                            isOpen={showQuoteConfirmModal}
                            onClose={() => setShowQuoteConfirmModal(false)}
                            userEmail={user.email} // This is used for the "Use my profile email" default
                            onConfirm={confirmAndSendQuote}
                        />
                    )}

                </div>
            </div>
            {showContactModal && (
                <ContactAdminModal
                    isOpen={showContactModal}
                    onClose={() => setShowContactModal(false)}
                    product={product}
                    user={user}
                />
            )}
            {showInquiryModal && (
                <InquiryModal
                    isOpen={showInquiryModal}
                    onClose={() => setShowInquiryModal(false)}
                    product={product}
                    onSendInquiry={sendInquiry}
                />
            )}
            {showDieselQuoteModal && (
                <DieselQuoteModal
                    isOpen={showDieselQuoteModal}
                    onClose={() => setShowDieselQuoteModal(false)}
                    product={product}
                    onSendQuote={sendDieselQuote}
                />
            )}
            {imageToEdit && (
                <ImageBlurEditor
                    imageUrl={imageToEdit}
                    onSave={handleSaveBlurredImage}
                    onClose={() => setImageToEdit(null)}
                />
            )}
            {showWatermarkSelector && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
                        <div className="p-6 border-b flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Add Watermark to Image</h3>
                                <p className="text-sm text-gray-600 mt-1">Select an image to apply the MM watermark</p>
                            </div>
                            <button onClick={() => setShowWatermarkSelector(false)} className="text-gray-400 hover:text-gray-600">
                                <i className="fas fa-times text-xl"></i>
                            </button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                                <p className="text-sm text-amber-800">
                                    <i className="fas fa-info-circle mr-2"></i>
                                    <strong>Note:</strong> Only one image can be watermarked at a time.
                                </p>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-4">
                                {productImages.map((img, index) => (
                                    <div key={img + index} className="relative">
                                        <input
                                            type="radio"
                                            id={`watermark-img-${index}`}
                                            name="watermark-image"
                                            value={img}
                                            checked={selectedImageForWatermark === img}
                                            onChange={(e) => setSelectedImageForWatermark(e.target.value)}
                                            className="sr-only"
                                        />
                                        <label
                                            htmlFor={`watermark-img-${index}`}
                                            className={`cursor-pointer block rounded-lg overflow-hidden border-2 transition-all ${
                                                selectedImageForWatermark === img
                                                    ? 'border-blue-500 ring-2 ring-blue-200'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <img
                                                src={optimizeImageSrc(img, 300)}
                                                alt={`Image ${index + 1}`}
                                                className="w-full h-32 object-cover"
                                            />
                                            <div className="p-2 bg-white">
                                                <div className="flex items-center">
                                                    <div className={`w-4 h-4 rounded-full border-2 mr-2 flex items-center justify-center ${
                                                        selectedImageForWatermark === img
                                                            ? 'border-blue-500 bg-blue-500'
                                                            : 'border-gray-300'
                                                    }`}>
                                                        {selectedImageForWatermark === img && (
                                                            <i className="fas fa-check text-white text-xs"></i>
                                                        )}
                                                    </div>
                                                    <span className="text-sm text-gray-700">Image {index + 1}</span>
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                ))}
                            </div>
                            
                            {actionError && (
                                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg">
                                    {actionError}
                                </div>
                            )}
                            
                            {actionSuccess && (
                                <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg">
                                    {actionSuccess}
                                </div>
                            )}
                        </div>
                        
                        <div className="p-6 border-t bg-gray-50 rounded-b-xl flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowWatermarkSelector(false);
                                    setSelectedImageForWatermark(null);
                                    setActionError(null);
                                    setActionSuccess(null);
                                }}
                                className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                                disabled={isWatermarkProcessing}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleAddWatermark(selectedImageForWatermark)}
                                disabled={!selectedImageForWatermark || isWatermarkProcessing}
                                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {isWatermarkProcessing ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin mr-2"></i>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-tint mr-2"></i>
                                        Apply Watermark
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showSpecsModal && <VariantSpecsModal variant={product} onClose={() => setShowSpecsModal(false)} />}
            {showRebuildConfigModal && (
                <RebuildConfigurationModal 
                    isOpen={showRebuildConfigModal} 
                    onClose={() => setShowRebuildConfigModal(false)} 
                    rebuildConfig={rebuildConfig} 
                    onConfigChange={handleRebuildSelection} 
                    sellerRebuildDetails={sellerRebuildDetails} 
                />
            )}
            </React.Fragment>
        );
}
